import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Map as MapLibreMap } from 'maplibre-gl';
import {
  Alert, AlertTitle, Box, Button, CircularProgress, Dialog, DialogActions,
  Chip, DialogContent, DialogTitle, Divider, Fab, IconButton, MenuItem,
  Paper, Snackbar,
  Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';

import { useLocation, useNavigate } from 'react-router-dom';

import { api, ApiError, basemapUrlFor, cancelPlan } from '../api/client';
import type {
  Category, City, PlanRequest, Poi, RouteAlternative, RoutePlan, RouteStyle, TravelMode,
} from '../api/types';
import { PlannerForm, BREAK_CATEGORIES, type PinnedPoint } from '../components/PlannerForm';
import { ExportRouteButton } from '../components/ExportRouteButton';
import { directionsForPlan } from '../export/dayRequest';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';

import { CHARACTER_CATEGORIES, CHARACTER_SENSE, placesAlongRoute } from '../map/characterPlaces';
import { AssistantPanel } from '../components/AssistantPanel';
import { FEATURES } from '../config/features';
import { ItineraryPanel } from '../components/ItineraryPanel';
import { ThemedAlternatives } from '../components/ThemedAlternatives';
import { RouteVariants } from '../components/RouteVariants';
import { formatClock } from '../util/time';
import { endsTitle } from '../util/routeTitle';
import { PinnedStopDialog } from '../components/PinnedStopDialog';
import { PlanningProgress } from '../components/PlanningProgress';
import { PlaceChooserDialog } from '../components/PlaceChooserDialog';
import { pickDefaultCity, rememberCity } from '../util/defaultCity';
import type { ViewBox } from '../util/viewBox';
import { covers, wider } from '../util/viewBox';
import { PlaceDetailsDialog } from '../components/PlaceDetailsDialog';
import { PlacePickerDialog, type PickedPoi } from '../components/PlacePickerDialog';
import { MapView } from '../components/MapView';
import { MapOnlyToggle } from '../components/MapOnlyToggle';
import { CategoryPanel } from '../components/CategoryPanel';
import { buildCategoryColours, DEFAULT_GROUP_COLOUR } from '../theme';


/**
 * Whether themed alternatives are offered at all.
 *
 * Back on. It was hidden while a route style could be combined with others,
 * which made "pick several styles" and "pick a theme" two answers to the
 * same question; a style is one choice now, and a theme is the other thing
 * entirely — a quota reserved before the solve rather than a hint fed to the
 * ranking.
 */
const SHOW_THEMES = true;

/**
 * The city the planner opens on.
 *
 * Matched on slug so the setting survives a database restore where the ids
 * differ, and so it reads as a place rather than a UUID in the Admin list.
 */
/**
 * Warnings that mean "your instruction was not fully honoured".
 *
 * The rest are notes about the search — how many places were ranked, what did
 * not fit the day — and belong where they are, in the schedule. These are a
 * different kind of thing: the route on screen does not do what was asked of
 * it, and the map is where somebody is looking when they need to know that.
 */
const SHORTFALL = /no way round|no route could|could not be fully bypassed|avoids none of them|passes \d+ that sit/i;

/** What the backend says about the road classes the route runs on. */

/**
 * A request reduced to what actually changes the route.
 *
 * Labels are dropped, because a name arriving from a lookup after a pin is
 * placed is not a change worth re-routing for. Written defensively: not
 * every request compared here was built by the form — the assistant hands
 * back partial ones, which may carry no `avoid` section at all.
 *
 * Used for two things that must agree: deciding whether the form has moved
 * since the last attempt, and finding a plan already computed for exactly
 * this request.
 */
/**
 * JSON with the keys in a settled order, at every depth.
 *
 * `JSON.stringify` writes keys in the order they were inserted, so two
 * objects holding exactly the same trip compare as different when they were
 * assembled in a different order — and the two compared below always are.
 * The form builds its draft `cityId, start, end, mode, ...`; the assistant's
 * request is built from a block of defaults with `...nextDraft` spread over
 * it, which puts `mode, startTime, ...` first and `cityId, start, end` after.
 *
 * Arrays keep their order, because theirs is meaningful — the order of the
 * stops is the route.
 */
function stable(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stable);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.keys(value as Record<string, unknown>)
        .sort()
        .map((key) => [key, stable((value as Record<string, unknown>)[key])]),
    );
  }

  return value;
}

/**
 * What makes two plan requests the same trip.
 *
 * Read by the live re-plan loop to decide whether the route on screen still
 * answers the form beside it. It compared insertion order as well as content,
 * which meant a plan the assistant had just applied never matched the form it
 * had just filled in: the loop fired, re-planned from the form, and replaced
 * the route the reply had described with a different one. The chat said one
 * thing and the map showed another.
 */
/**
 * A wall-clock time, so many minutes later.
 *
 * The itinerary shows both minutes-from-start and an HH:MM, and switching
 * between two ways round moves the second without touching the first stop.
 * Anything that is not HH:MM is handed back untouched — this is for display,
 * and a value it does not recognise is better shown than blanked.
 */
function clockAfter(from: string, minutes: number): string {
  const [h, m] = String(from ?? "").split(":");
  if (!/^\d{1,2}$/.test(h ?? "") || !/^\d{2}$/.test(m ?? "")) return from;

  const total = ((Number(h) * 60 + Number(m) + Math.round(minutes)) % 1440 + 1440) % 1440;

  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}


/**
 * Whether to offer a longer day when places did not fit.
 *
 * Annotated `boolean` rather than left as the literal `false`: a literal
 * makes the rest of the condition unreachable to the compiler, which drops
 * the narrowing the branch depends on and turns a hidden block into a
 * screenful of errors. Typed this way the branch is still checked, so it
 * cannot rot while it is switched off.
 */
const SHOW_BUDGET_SUGGESTION: boolean = false;

function requestSignature(request: PlanRequest): string {
  return JSON.stringify(stable({
    ...request,
    mustVisitPoints: (request.mustVisitPoints ?? []).map(({ lat, lng }) => ({ lat, lng })),
    avoid: {
      ...(request.avoid ?? {}),
      points: (request.avoid?.points ?? []).map(({ lat, lng }) => ({ lat, lng })),
    },
    start: request.start?.kind === 'coords'
      ? { kind: 'coords', lat: request.start.lat, lng: request.start.lng } : request.start,
    end: request.end?.kind === 'coords'
      ? { kind: 'coords', lat: request.end.lat, lng: request.end.lng } : request.end,
  }));
}

export function PlannerPage() {
  const [cities, setCities] = useState<City[] | null>(null);
  const [city, setCity] = useState<City | null>(null);
  const [styles, setStyles] = useState<RouteStyle[]>([]);
  const [settings, setSettings] = useState<Record<string, unknown>>({});

  // Category browser
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [poiData, setPoiData] = useState<unknown>(null);
  /*
    The ground the dots have been fetched for.

    Grown beyond the view, so a short pan stays inside what has already
    been read and costs nothing; replaced only when the map shows ground
    this box does not hold, which is what zooming out or travelling does.
  */
  const [poiBox, setPoiBox] = useState<ViewBox | null>(null);
  const watchView = useCallback((bounds: ViewBox) => {
    setPoiBox((current) => (current && covers(current, bounds) ? current : wider(bounds)));
  }, []);

  // Closed by default: the rail is a browsing aid, not part of the main flow.
  const [showCategories, setShowCategories] = useState(false);
  const [picked, setPicked] = useState<PickedPoi | null>(null);

  // Endpoints live here so a marked spot can become start or destination.
  const [start, setStart] = useState<Poi | null>(null);
  const [end, setEnd] = useState<Poi | null>(null);
  const [startPin, setStartPin] = useState<PinnedPoint | null>(null);
  const [endPin, setEndPin] = useState<PinnedPoint | null>(null);
  const [visitPins, setVisitPins] = useState<PinnedPoint[]>([]);
  const [avoidPins, setAvoidPins] = useState<PinnedPoint[]>([]);
  /*
   * Points the route is bent through, from dragging the line.
   *
   * Kept apart from the stop lists on purpose: these shape the path and are
   * not places to visit, so they carry no dwell time and never appear in the
   * itinerary. `afterStopIndex` records which leg was dragged.
   */
  const [viaPoints, setViaPoints] = useState<
    { lat: number; lng: number; afterStopIndex: number }[]
  >([]);
  /*
   * A visiting order arranged by hand, or null to let the optimiser choose.
   *
   * Page state rather than a one-off request, so that a later edit re-plans
   * with the arrangement intact instead of silently reverting to the
   * optimiser's own idea of the order.
   */
  const [pinnedOrder, setPinnedOrder] = useState<string[] | null>(null);
  /*
   * Stay times chosen per stop, keyed by place id or `pin-N`.
   *
   * Only what the traveller changed is held here; anything absent keeps the
   * default for its category, so the map of overrides stays small and a
   * change of mind is a deletion rather than a value to maintain.
   */
  const [stopDwellMinutes, setStopDwellMinutes] = useState<Record<string, number>>({});
  /**
   * How each leg is travelled, keyed by the stop it leaves.
   *
   * Held here beside the stay times for the same reason: both are edited on
   * the itinerary, both belong to the request, and the draft loop re-plans
   * from them.
   */
  const [legModes, setLegModes] = useState<Record<string, TravelMode>>({});
  const handleLegModeChange = useCallback((key: string, mode: TravelMode) => {
    setLegModes((current) => ({ ...current, [key]: mode }));
  }, []);
  /*
   * Break lengths, edited from the itinerary rather than the form.
   *
   * Only a traveller's explicit change is held here. Anything unset falls
   * back to the configured default, so changing that default in Admin takes
   * effect without anyone having to clear a value they never set.
   */
  const [breakOverride, setBreakOverride] = useState<{ lunch?: number; coffee?: number }>({});
  const [breaksEnabled, setBreaksEnabled] = useState({ lunch: false, coffee: false });
  /*
   * A place chosen for each break, or null to let the planner find one.
   *
   * Held here rather than in the form because a break can also be removed
   * from the itinerary, and both paths have to agree about what it is.
   */
  const [breakPlaces, setBreakPlaces] = useState<{ lunch: Poi | null; coffee: Poi | null }>({
    lunch: null,
    coffee: null,
  });
  /*
   * Places removed from the itinerary by hand.
   *
   * A removed suggestion has to be remembered, or the next re-plan simply
   * suggests it again and the removal looks broken.
   */
  const [excludedPoiIds, setExcludedPoiIds] = useState<string[]>([]);
  /** Which of the other ways round is drawn, so the list can say so. */
  const [appliedVariant, setAppliedVariant] = useState<number | null>(null);
  /*
   * Avoidance radii that differ from the slider.
   *
   * Only what was deliberately changed is held, so raising the slider still
   * moves everything the traveller never singled out.
   */
  const [avoidDistanceByPoiId, setAvoidDistanceByPoiId] = useState<Record<string, number>>({});
  const [avoidDistanceByCategoryKey, setAvoidDistanceByCategoryKey] =
    useState<Record<string, number>>({});

  const handleAvoidDistanceOverride = useCallback(
    (scope: 'poi' | 'category', key: string, metres: number | null) => {
      const apply = (current: Record<string, number>) => {
        if (metres === null) {
          // Cleared, so the entry goes rather than being pinned to the value
          // it happened to inherit.
          const { [key]: _removed, ...rest } = current;
          return rest;
        }
        return { ...current, [key]: metres };
      };
      if (scope === 'poi') setAvoidDistanceByPoiId(apply);
      else setAvoidDistanceByCategoryKey(apply);
    },
    [],
  );
  /**
   * One place's own stay time, or back to its category's default.
   *
   * Clearing DELETES the key rather than storing a number, exactly as the
   * per-place avoidance distances do: the map of overrides stays small, and a
   * default changed in Admin takes effect without anybody having to clear a
   * value they never set.
   */
  const handleStopDwellOverride = useCallback((poiId: string, minutes: number | null) => {
    setStopDwellMinutes((current) => {
      const next = { ...current };
      if (minutes === null) delete next[poiId];
      else next[poiId] = minutes;
      return next;
    });
  }, []);

  /* Bumped to remount the form, which is how its internal fields are reset. */
  /*
   * The live map, held only so the export can take a picture of it.
   *
   * A ref rather than state: nothing on this page re-renders because the map
   * finished loading, and making it state would redraw everything for the
   * benefit of a button that is read once, on a click.
   */
  const exportMap = useRef<MapLibreMap | null>(null);

  const [formKey, setFormKey] = useState(0);
  const [confirmReset, setConfirmReset] = useState(false);
  /*
   * Whether the side panel is showing, on a wide screen.
   *
   * Once a route is planned the map is the thing being read, and the panel is
   * 400px of it. Folding is remembered for the session rather than reset on
   * every re-plan, which would undo the choice constantly.
   */
  const [panelOpen, setPanelOpen] = useState(true);
  /*
   * Whether the panel is showing on a narrow screen, where it replaces the map
   * rather than sitting beside it.
   *
   * Separate from `editing`, which decides whether the panel shows the form or
   * the finished route. Conflating the two meant the only way to bring the
   * panel back was to open the form — so a button marked "Itinerary" showed
   * anything but.
   */
  const [panelOnMobile, setPanelOnMobile] = useState(false);
  const [hoveredAlt, setHoveredAlt] = useState<number | null>(null);
  /** A spot clicked on empty map, waiting for the traveller to give it a role. */
  const [markedPoint, setMarkedPoint] = useState<{ lat: number; lng: number } | null>(null);

  /*
    One stop, picked in either view and shown in both.

    The map and the itinerary are two readings of the same round, and until
    now picking a stop in one did nothing to the other: a marker click
    highlighted a row that might be forty stops down an unscrolled list, and
    a row click did not move the map at all.

    Two signals rather than one, because highlighting already happens on
    HOVER. A map that chased the cursor down the list, or a list that
    scrolled under the cursor reading it, would each be worse than no link.
    Both carry a nonce so picking the same stop twice works: after panning
    away, clicking the same row again means "take me back".
  */
  const [focusStop, setFocusStop] = useState<{ index: number; nonce: number } | null>(null);
  const [revealStop, setRevealStop] = useState<{ index: number; nonce: number } | null>(null);


  // Place selections live here, not in the form, so the map can add to them.
  const [mustVisit, setMustVisit] = useState<Poi[]>([]);

  /*
   * Of those, the ones the traveller never actually named.
   *
   * Pinning an order has to make every stop explicit — the server keeps an
   * order by being given the places that make it — so one drag turns a
   * hundred and thirty places the planner proposed into places "asked for".
   * They belong in the request. They do not belong in the list of places
   * somebody chose, which they bury, nor as a hundred and thirty pins over a
   * route that already draws them.
   *
   * So the request keeps `mustVisit` whole and the screen reads
   * `namedPlaces`. Choosing one from the picker takes it off this list, and
   * letting the order go (see unpinOrder) empties it: without an order to
   * hold, nothing is holding them.
   */
  /* The catalogue, open over the form. See PlaceChooserDialog. */
  const [browsing, setBrowsing] = useState(false);

  const [pinnedByOrder, setPinnedByOrder] = useState<Set<string>>(new Set());

  const namedPlaces = useMemo(
    () => mustVisit.filter((p) => !pinnedByOrder.has(p.id)),
    [mustVisit, pinnedByOrder],
  );

  /* The other half, which the request still carries. Read from the list, so
     an id left behind by a removed stop cannot resurrect it. */
  const orderHeld = useMemo(
    () => mustVisit.filter((p) => pinnedByOrder.has(p.id)).map((p) => p.id),
    [mustVisit, pinnedByOrder],
  );
  const [avoid, setAvoid] = useState<Poi[]>([]);
  const [avoidDistanceM, setAvoidDistanceM] = useState(500);
  /*
   * How long the round has: five hours to open on, and always a number.
   *
   * The field that sets it is offered only where it does something — in the
   * kinds mode, with at least one kind of site chosen — so in the only
   * position it appears it always has an answer. A length is a ceiling
   * rather than a target: it decides how many of those kinds fit, and with
   * nothing to draw from it cannot add a stop at all.
   */
  const [availableMinutes, setAvailableMinutes] = useState(300);
  const [avoidCategories, setAvoidCategories] = useState<string[]>([]);
  /** Coordinates of places in the avoided categories, for drawing their zones. */
  const [avoidCategoryPoints, setAvoidCategoryPoints] = useState<
    { lat: number; lng: number; categoryKey: string }[]
  >([]);

  const [plan, setPlan] = useState<RoutePlan | null>(null);
  /*
   * Whether the form is open, kept separate from whether a plan exists.
   *
   * "Change plan" used to clear the plan, which wiped the route off the map
   * while you were editing — losing the very thing you were adjusting
   * against. The plan now stays drawn until a new one replaces it.
   */
  const [editing, setEditing] = useState(false);

  /*
   * The assistant, and what it hands back.
   *
   * It builds the same PlanRequest the form does, so adopting its answer is
   * the same two steps a submitted form takes: draw the plan, and remember
   * the request that produced it. `lastAttempt` is set with `lastRequest`
   * because the live re-plan loop compares against it — without that, the
   * form's own draft would be re-planned straight over the top of whatever
   * the assistant just built.
   */
  const [assistantOpen, setAssistantOpen] = useState(false);
  /** Mounted from the first time it is opened, so shutting it forgets nothing. */
  const [assistantEverOpen, setAssistantEverOpen] = useState(false);
  /*
   * Several days, asked for on the one-day screen.
   *
   * A trip is a different planner, so this is a hand-off rather than an
   * adoption: the trip and the request that made it travel in the router's
   * own state, and the multi-day screen opens on them. Asking for a week and
   * being shown a day would be the wrong answer to the right question.
   *
   * The conversation goes with them. The two screens are mounted one at a
   * time, so the panel here is unmounted by the navigation and the one there
   * starts shut — which made asking for a week close the chat that had just
   * been asked. It is the same component and it resumes the same
   * conversation, so the only thing that has to cross is that it was open.
   */
  const navigate = useNavigate();
  const handleAssistantTrip = useCallback(
    (tripRequest: unknown, trip: unknown) => {
      void navigate('/plan/trip', { state: { tripRequest, trip, assistantOpen: true } });
    },
    [navigate],
  );

  /** Interests, here rather than in the form because the assistant sets them. */
  const [interestKeys, setInterestKeys] = useState<string[]>([]);
  /** A trip built by the assistant, for the form to open with. */
  const [seed, setSeed] = useState<Partial<PlanRequest> | null>(null);

  /*
   * The assistant's answer, adopted by the page.
   *
   * Not just drawn — FILLED IN. Everything it decided is put where the
   * traveller would have put it: the endpoints in their pickers, the stops in
   * "places to visit", the avoided categories in their chips, the hours in
   * the slider. The form is then remounted around it, which is what makes the
   * conversation and the form two ways of describing one trip rather than two
   * trips that happen to agree.
   *
   * Place ids are resolved to real places because the pickers show names; a
   * lookup that fails leaves that field alone rather than emptying it.
   */
  const handleAssistantResult = useCallback(
    async (
      nextDraft: Partial<PlanRequest>,
      nextPlan: RoutePlan | null,
      options?: { restore?: boolean; startedAt?: number },
    ) => {
      if (!nextDraft?.cityId) return;
      /*
       * Not if the form was cleared while this turn was running.
       *
       * A turn takes tens of seconds and comes back holding a whole trip. A
       * traveller who cleared the form in the meantime watched the route they
       * had just cleared draw itself again — and, because the answer restores
       * the request too, the live re-plan then started on top of it. What
       * they asked for is gone; the answer to it is stale.
       *
       * Measured against when the TURN began, which is the question this was
       * always asking and never able to answer. It compared the clear to the
       * clock instead, so a reset silently swallowed every answer for the two
       * minutes after it — a conversation begun AFTER clearing the form,
       * which is the ordinary way to start one, came back with a whole plan
       * in the transcript and an untouched form beside it. A clear that
       * happened before the question was asked is not a clear of its answer.
       */
      const stale = options?.startedAt != null
        ? clearedAt.current > options.startedAt
        : clearedAt.current > 0 && Date.now() < clearedAt.current + STALE_AFTER_CLEAR_MS;
      if (!options?.restore && stale) {
        setToast('The form was cleared, so that answer was not applied');
        return;
      }

      /*
       * Only ever looked up by id.
       *
       * A model that puts a place NAME where an id belongs used to reach the
       * API as /api/pois/Garfield%20Heights, which is a 500 rather than an
       * answer. The tools refuse to set one now; this refuses to fetch one.
       */
      const isId = (value: string) =>
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
      const place = async (id: string) =>
        (isId(id) ? api.poi(id).catch(() => null) : null);
      const endpoint = async (
        ref: PlanRequest['start'] | undefined,
        setPlace: (poi: Poi | null) => void,
        setPin: (pin: PinnedPoint | null) => void,
      ) => {
        if (!ref) return;
        if (ref.kind === 'coords') {
          setPin({ lat: ref.lat, lng: ref.lng, label: 'Chosen by the assistant' });
          setPlace(null);
          return;
        }
        const found = await place(ref.poiId);
        if (found) { setPlace(found); setPin(null); }
      };

      await endpoint(nextDraft.start, setStart, setStartPin);
      await endpoint(nextDraft.end, setEnd, setEndPin);

      if (nextDraft.mustVisitPoiIds) {
        const found = await Promise.all(nextDraft.mustVisitPoiIds.map(place));
        setMustVisit(found.filter((p): p is Poi => p !== null));
      }
      if (nextDraft.avoid?.poiIds) {
        const found = await Promise.all(nextDraft.avoid.poiIds.map(place));
        setAvoid(found.filter((p): p is Poi => p !== null));
      }
      if (nextDraft.avoid?.categoryKeys) {
        /*
         * Only keys this city actually has.
         *
         * A key that does not exist avoids nothing, and the chip says so in
         * the least useful way available — "All police station (?)" beside a
         * route that passes every police station. Dropped here so the form
         * never shows an avoidance that is not happening.
         */
        /*
         * Filtered only against categories that are KNOWN. Before they load
         * the set is empty, which is not "this city has none" — and filtering
         * against it threw away every avoidance the assistant had set, which
         * matters on a page adopting a day the moment it mounts.
         */
        const known = new Set(categories.map((c) => c.key));
        setAvoidCategories(known.size === 0
          ? nextDraft.avoid.categoryKeys
          : nextDraft.avoid.categoryKeys.filter((key) => known.has(key)));
      }
      if (nextDraft.avoid?.distanceM) setAvoidDistanceM(nextDraft.avoid.distanceM);
      /*
       * And the per-place and per-category radii it set.
       *
       * These were planned with and then dropped on the way back. "Avoid
       * police stations, 300 m" planned at 300 and left the form saying 500:
       * the categories were adopted and the distance beside them was not, so
       * the assistant's own answer became untrue the moment it arrived — and
       * the next re-plan used 500, because the form is what re-plans.
       *
       * Worse than a distance that never applied, since the reply said it
       * had. Adopted here so the form agrees with the route that was drawn.
       */
      if (nextDraft.avoid?.distanceByCategoryKey) {
        setAvoidDistanceByCategoryKey(nextDraft.avoid.distanceByCategoryKey);
      }
      if (nextDraft.avoid?.distanceByPoiId) {
        setAvoidDistanceByPoiId(nextDraft.avoid.distanceByPoiId);
      }
      // Per-leg modes came with the multi-city branch; an assistant that sets
      // one has to reach the itinerary's own picker, or the two disagree
      // about how a leg is travelled.
      if (nextDraft.legModes) setLegModes(nextDraft.legModes);
      if (nextDraft.availableMinutes) setAvailableMinutes(nextDraft.availableMinutes);
      if (nextDraft.interestCategoryKeys) setInterestKeys(nextDraft.interestCategoryKeys);
      setBreaksEnabled({
        lunch: Boolean(nextDraft.lunch),
        coffee: Boolean(nextDraft.coffee),
      });
      setBreakOverride({
        ...(nextDraft.lunch?.durationMinutes ? { lunch: nextDraft.lunch.durationMinutes } : {}),
        ...(nextDraft.coffee?.durationMinutes ? { coffee: nextDraft.coffee.durationMinutes } : {}),
      });

      // Remounted so the fields the form owns — mode, style, times, date —
      // open on what was asked for. `formKey` is the existing mechanism.
      setSeed(nextDraft);
      setFormKey((current) => current + 1);

      /*
       * Completed before it is stored. Everything downstream — the compare
       * loop, the save dialog, a re-plan — reads a whole PlanRequest, and a
       * partial one from the assistant would be a missing section waiting to
       * be dereferenced.
       */
      const request = {
        mode: 'car' as const,
        startTime: '09:00',
        availableMinutes,
        interestCategoryKeys: [],
        mustVisitPoiIds: [],
        mustVisitPoints: [],
        excludePoiIds: [],
        safetyPreference: 'prefer' as const,
        ...nextDraft,
        avoid: { poiIds: [], categoryKeys: [], points: [], ...(nextDraft.avoid ?? {}) },
      } as PlanRequest;
      setLastRequest(request);
      // Set with it, because the live re-plan loop compares against
      // lastAttempt — without this the form's own draft would be re-planned
      // straight over the top of what the assistant just built.
      setLastAttempt(request);
      if (nextPlan) {
        setPlan(nextPlan);
        setError(null);
        setEditing(false);
      }
    },
    [categories, availableMinutes],
  );

  /*
   * A day handed back from the multi-day screen.
   *
   * The mirror of the hand-off going the other way, and it was missing: this
   * page read nothing from the router at all, so asking the assistant on the
   * trip screen for a single day switched planner and arrived at an empty
   * form. The two screens are mounted one at a time and share no state, so
   * the router is the only thing that crosses between them.
   *
   * Held rather than adopted on the spot: adoption needs a city, and reads
   * the city's categories to decide which avoidances are real. Both arrive a
   * moment after mount, so the answer waits for them.
   */
  const location = useLocation();
  const [handedDay, setHandedDay] = useState<
    { draft: Partial<PlanRequest>; plan: RoutePlan | null } | null
  >(null);
  useEffect(() => {
    const handed = location.state as {
      draft?: Partial<PlanRequest>; plan?: RoutePlan | null; assistantOpen?: boolean;
    } | null;
    if (!handed?.draft) return;
    setHandedDay({ draft: handed.draft, plan: handed.plan ?? null });
    if (handed.assistantOpen) {
      setAssistantEverOpen(true);
      setAssistantOpen(true);
    }
    // Cleared from history at once, so a reload does not resurrect a day the
    // traveller has moved on from.
    void navigate('.', { replace: true, state: null });
  }, [location.state, navigate]);

  useEffect(() => {
    // `lastCityId` having caught up means the city effect has run and done
    // its clearing, so there is nothing left to wipe what is adopted here.
    if (!handedDay || !city || lastCityId.current !== city.id) return;
    setHandedDay(null);
    /*
     * `restore`, because this is not an answer to anything typed on THIS
     * page — the stale-after-clearing guard is about a turn overtaking a
     * traveller who cleared the form, and a page that has only just mounted
     * has had nothing cleared on it.
     */
    void handleAssistantResult(handedDay.draft, handedDay.plan, { restore: true });
  }, [handedDay, city, categories, handleAssistantResult]);

  /*
   * The request the form currently describes, reported on every change.
   *
   * Once a route exists this drives it: any edit re-plans, so the map always
   * shows the route the form describes rather than a stale one from before
   * the last change.
   */
  const [draft, setDraft] = useState<PlanRequest | null>(null);
  /*
   * Changing how the whole day travels clears the per-leg answers.
   *
   * They were given against a different question — "walk this bit, though" is
   * about a walking day — and leaving them behind means switching a walk to a
   * drive silently keeps three legs on foot.
   */
  const lastMode = useRef<TravelMode | null>(null);
  useEffect(() => {
    const mode = draft?.mode;
    if (!mode) return;
    if (lastMode.current && lastMode.current !== mode) setLegModes({});
    lastMode.current = mode;
  }, [draft?.mode]);

  /*
   * The place being read about, and what adding it would mean.
   *
   * The role matters because the same dialog is opened from three places. A
   * cafe reached from the lunch suggestions is being considered AS lunch, and
   * adding it as an ordinary stop would leave the break unfilled — the
   * opposite of what was asked.
   */
  /** The search corridor, while the traveller has asked to see it. */
  const [coverage, setCoverage] = useState<unknown | null>(null);
  /** City outlines, while somebody has asked to see them. */
  const [regions, setRegions] = useState<unknown | null>(null);
  const [details, setDetails] = useState<
    { poiId: string; role: 'visit' | 'lunch' | 'coffee' } | null
  >(null);
  const detailsPoiId = details?.poiId ?? null;
  const setDetailsPoiId = (poiId: string | null) =>
    setDetails(poiId ? { poiId, role: 'visit' } : null);
  /*
   * Suggested places the map is drawing, and which one the cursor is on.
   *
   * Held here rather than in the form because the map is here. Empty whenever
   * the traveller has switched the map off, or the corridor has changed.
   */
  const [suggestionPreview, setSuggestionPreview] = useState<{
    places: { poiId: string; name: string; lat: number; lng: number }[];
    hoveredPoiId: string | null;
    /* Which list these belong to, so clicking one opens the right thing. */
    role: 'visit' | 'lunch' | 'coffee';
  }>({ places: [], hoveredPoiId: null, role: 'visit' });
  /* Which themed alternative is currently drawn, so the list can mark it. */
  const [appliedTheme, setAppliedTheme] = useState<string | null>(null);
  /*
   * The route that was on screen before an alternative replaced it.
   *
   * Applying one is a preview, and a preview with no way back is a
   * replacement: the emphasis on the form still says Scenic Way while the
   * map shows Safe & Direct, and the only route home was to change the form
   * and re-plan the day. Held from the first apply only, so a second
   * alternative still returns to the original rather than to the first
   * alternative.
   */
  const [beforeTheme, setBeforeTheme] = useState<RoutePlan | null>(null);

  /**
   * Promotes a place to one the planner must keep.
   *
   * Fetched rather than taken from the plan, because a dropped place is only
   * a name and an id there — `mustVisit` holds whole records so the form can
   * list it like any other requested place.
   */
  /*
    A new stop means the pinned order no longer describes the round.

    Dragging a stop pins the whole itinerary and tells the optimiser to leave
    it alone — which is right for the arrangement that was dragged, and wrong
    for every round after it. The lock outlived the thing it was made for: add
    a place afterwards and it was appended to an order that never mentioned
    it, with the solve skipped and no suggestions, so the round visibly
    doubled back and nothing said why.

    Removing a stop already cleared it, for the same reason and in the same
    words. Adding one is the other half.
  */
  const unpinOrder = useCallback(() => {
    setPinnedOrder(null);
    /*
      And with it the places that were only there to hold it.

      Kept, they would go on forcing a hundred and thirty stops onto a round
      whose order has just been abandoned — every one of them a place the
      planner suggested once and can suggest again.
    */
    if (pinnedByOrder.size > 0) {
      setMustVisit((list) => list.filter((p) => !pinnedByOrder.has(p.id)));
      setPinnedByOrder(new Set());
    }
  }, [pinnedByOrder]);

  /*
    Why a place cannot be a stop on this round, or empty if it can.

    The two ends are the round; a stop is somewhere it passes BETWEEN them.
    Adding one of the ends as a stop asks the planner to visit a place the
    route is already at, and it obliges — with a second arrival at the same
    coordinates, a travel leg of zero, and a stay nobody wanted.

    Checked at every way in rather than only in the picker, because the map
    has two of its own: the role dialog on a clicked place, and the add
    button on a place panel.
  */
  const reservedAs = useCallback((id: string) => {
    if (start?.id === id) return 'That is where this round starts';
    if (end?.id === id) return 'That is where this round ends';

    return '';
  }, [start, end]);

  /* A mode taken from a failure alert's offer. See PlannerForm. */
  const [requestedMode, setRequestedMode] =
    useState<{ mode: TravelMode; token: number } | null>(null);
  const [lastRequest, setLastRequest] = useState<PlanRequest | null>(null);
  const [planning, setPlanning] = useState(false);
  /* Only an explicit submit; a background re-plan must not disable the form. */
  const [submitting, setSubmitting] = useState(false);
  /*
   * The last request sent, whether or not it worked.
   *
   * Distinct from `lastRequest`, which is the one behind the route currently
   * drawn. Comparing against this is what stops a failing request being sent
   * again and again with nothing changed.
   */
  const [lastAttempt, setLastAttempt] = useState<PlanRequest | null>(null);

  /*
   * Back to the top when the panel changes what it is showing.
   *
   * The form is taller than the panel, so "Plan the route" is reached by
   * scrolling to the bottom — and the itinerary that replaces it inherits
   * that offset, opening somewhere in the middle of the stops with the
   * totals and the ways round above the fold. The same in reverse: "Change
   * plan" from halfway down the itinerary opened the form halfway down.
   *
   * Keyed on whether a plan is being shown rather than on the plan itself,
   * so re-planning the day one is already reading does not yank it back.
   */
  const controlPanel = useRef<HTMLDivElement | null>(null);
  const showingPlan = Boolean(plan) && !editing;
  useEffect(() => {
    controlPanel.current?.scrollTo({ top: 0 });
  }, [showingPlan]);
  const [error, setError] = useState<ApiError | null>(null);
  const [highlighted, setHighlighted] = useState<number | null>(null);

  /*
   * Whether the places the round only passes are put down.
   *
   * Held here rather than in the map because the schedule hides them in
   * step: one switch, two panels, so the list and the line always agree
   * about what is being shown. Starts put down, and only means anything
   * where there are enough of them to be in the way.
   */
  const [passedHidden, setPassedHidden] = useState(true);

  const [toast, setToast] = useState<string | null>(null);

  /*
   * Tracked separately from the settings themselves: an empty object is a
   * perfectly valid answer, so `{}` cannot distinguish "no settings" from
   * "not asked yet" — and the city default below needs that distinction.
   */
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  /*
    Whether this deployment wants the planning assistant at all.

    `FEATURES.planningAgent` says whether it is in the build; this says whether
    the operator has switched it on. Unset is off, as Admin has always read it.
  */
  const assistantEnabled = settings['ai.enabled'] === true;

  useEffect(() => {
    api.settings()
      .then(setSettings)
      .catch(() => setSettings({}))
      .finally(() => setSettingsLoaded(true));
  }, []);

  useEffect(() => {
    api.cities().then(setCities).catch((err: ApiError) => setError(err));
  }, []);

  /*
   * Which city to open on is a decision, not an accident of ORDER BY.
   *
   * Configured by slug in `planner.default_city_slug`, falling back to the
   * first ready city so an unset or stale value never leaves the planner with
   * no city at all.
   *
   * Waits for BOTH lists rather than choosing from whichever arrived first.
   * Settings start as an empty object, so choosing on the cities response
   * alone picked the fallback every time and then never reconsidered.
   */
  useEffect(() => {
    if (city || !cities?.length || !settingsLoaded) return;
    setCity(pickDefaultCity(cities, settings));
  }, [city, cities, settings, settingsLoaded]);

  /* So the next screen opens on it, rather than on the configured default. */
  useEffect(() => { rememberCity(city); }, [city]);

  /**
   * The ground this plan would search, once both ends are known.
   *
   * Everything that describes "what is around here" follows it — the counts
   * beside each category and the places drawn on the map — so choosing a
   * start and destination narrows both from the whole city to the route.
   * Within one city that is a refinement; across cities it is the difference
   * between describing the right ground and the wrong one entirely.
   */
  const corridor = useMemo(() => {
    const from = startPin ?? (start ? { lat: start.lat, lng: start.lng } : null);
    /*
     * A round trip has no second end, so the start stands in for it — which
     * is what the planner does too, and why its corridor becomes a circle
     * rather than a line.
     */
    const to = endPin ?? (end ? { lat: end.lat, lng: end.lng } : from);
    if (!from || !to) return undefined;
    return {
      startLat: from.lat, startLng: from.lng,
      endLat: to.lat, endLng: to.lng,
      // Taken from the draft the form reports, which is the only place that
      // knows what the traveller has actually chosen.
      ...(draft?.mode ? { mode: draft.mode } : {}),
      availableMinutes: draft?.availableMinutes ?? availableMinutes,
    };
  }, [start, startPin, end, endPin, draft?.mode, draft?.availableMinutes, availableMinutes]);

  // Switching city invalidates every place reference.
  /*
   * The city this page last held, so a CHANGE can be told from arriving at one.
   *
   * `city` starts null and is filled once the cities and settings arrive,
   * which this effect could not distinguish from somebody picking a different
   * city — so everything set before that fetch landed was wiped a moment
   * later. That is fatal to anything handed to a fresh page: a day passed
   * back from the multi-day screen, or a seeded form, was cleared before it
   * could be seen.
   */
  const lastCityId = useRef<string | null>(null);
  useEffect(() => {
    if (!city) return;
    api.routeStyles(city.id).then(setStyles).catch(() => setStyles([]));
    /*
     * The distance Admin configured, not the city's own column.
     *
     * That column is written when a city is added, is editable nowhere, and
     * holds 500 for every city — so starting the slider from it meant the
     * Admin setting was decoration. The planner reads the same setting, so
     * the slider now opens on the number the plan will actually use.
     *
     * Set on arrival as well as on a change: it is this city's default, not
     * a clearing of somebody's answer.
     */
    setAvoidDistanceM(Number(
      settings['planner.default_avoid_distance_m'] ?? city.defaultAvoidDistanceM,
    ));

    const previous = lastCityId.current;
    lastCityId.current = city.id;
    // Arriving at the first city is not a change; nothing on the page refers
    // to a city yet, so there is nothing to invalidate.
    if (previous === null || previous === city.id) return;

    setSelectedCategories([]);
    setPoiData(null);
    setMustVisit([]);
    setPinnedByOrder(new Set());
    setAvoid([]);
    setAvoidCategories([]);
    setAvoidCategoryPoints([]);
    setLegModes({});
    setStart(null); setEnd(null);
    setStartPin(null); setEndPin(null);
    setVisitPins([]); setAvoidPins([]);
    setPlan(null);
    setEditing(false);
    setError(null);
  }, [city]);

  /*
   * The counts, kept apart from the reset above.
   *
   * They have to follow the corridor — that is the whole point, so the number
   * beside each category describes the ground the route actually covers — but
   * the effect that reacts to a city change also clears the start and
   * destination. Watching the corridor from there would clear the endpoints
   * every time they moved, which is a loop, not a refresh.
   */
  useEffect(() => {
    if (!city) return;
    let cancelled = false;
    api.cityCategories(city.id, corridor)
      .then((c) => { if (!cancelled) setCategories(c); })
      .catch(() => { if (!cancelled) setCategories([]); });
    return () => { cancelled = true; };
  }, [city, corridor]);

  /*
   * The same categories counted over the ground AVOIDANCE covers.
   *
   * Browsing covers whole cities the route crosses; avoidance covers the
   * roads and their reach, and cannot cover more — the exclusion budget will
   * not carry a state's worth of buffers. Labelling an avoid chip with the
   * browsing number promised the plan would keep clear of 447 places when it
   * would keep clear of 160.
   */
  const [avoidCategoryCounts, setAvoidCategoryCounts] = useState<Category[]>([]);
  useEffect(() => {
    if (!city) return;
    let cancelled = false;
    api.cityCategories(city.id, corridor, corridor ? 'avoid' : undefined)
      .then((c) => { if (!cancelled) setAvoidCategoryCounts(c); })
      .catch(() => { if (!cancelled) setAvoidCategoryCounts([]); });
    return () => { cancelled = true; };
  }, [city, corridor]);

  /*
    Fetch the dots for whatever categories are ticked, over the ground in
    view.

    The whole city was nearly two megabytes of GeoJSON with every kind
    ticked, and the limit that held that down cuts the list by name — so
    the map drew everything from A to M and nothing after, which is not a
    map of anything. Asking for the view instead is a fraction of that and
    complete as far as it goes, and the box only changes when the map
    shows ground it has not read.
  */
  useEffect(() => {
    if (!city || selectedCategories.length === 0) {
      setPoiData(null);
      return;
    }
    let cancelled = false;
    api
      .poisGeoJson({
        cityId: city.id,
        categoryKeys: selectedCategories,
        limit: 4000,
        ...(poiBox ? { bbox: poiBox } : {}),
        ...corridor,
      })
      .then((data) => { if (!cancelled) setPoiData(data); })
      .catch(() => { if (!cancelled) setPoiData(null); });
    return () => { cancelled = true; };
  }, [city, selectedCategories, corridor, poiBox]);

  /*
   * An avoided category still has a proximity radius, so its zones must be
   * drawn like any other — otherwise "avoid all police stations at 500 m"
   * shows nothing on the map until the route is planned, and there is no way
   * to judge how much of the city it swallows.
   */
  useEffect(() => {
    /*
     * Only the drawn zones are cleared here.
     *
     * Endpoints and pins were being cleared in this branch too, which meant
     * that removing the last avoided category wiped the traveller's start,
     * destination and every marked spot — they belong to the city change
     * above, which is the only thing that actually invalidates a place.
     */
    if (!city || avoidCategories.length === 0) {
      setAvoidCategoryPoints([]);
      return;
    }
    let cancelled = false;
    api
      .poisGeoJson({
        /*
         * High enough to draw every place the plan will keep clear of.
         * Capped at 300 the map showed 300 of 447 zones, which is the same
         * quiet disagreement between the picture and the plan that the
         * counts beside it exist to prevent.
         */
        cityId: city.id, categoryKeys: avoidCategories, limit: 2000, ...corridor,
        // The ground avoidance actually covers, so the preview and the plan
        // agree on what will be kept clear.
        for: 'avoid',
      })
      .then((data) => {
        if (cancelled) return;
        const fc = data as {
          features?: {
            geometry: { coordinates: [number, number] };
            properties?: { categoryKey?: string };
          }[];
        };
        setAvoidCategoryPoints(
          (fc.features ?? []).map((f) => ({
            lng: f.geometry.coordinates[0],
            lat: f.geometry.coordinates[1],
            categoryKey: String(f.properties?.categoryKey ?? ''),
          })),
        );
      })
      .catch(() => { if (!cancelled) setAvoidCategoryPoints([]); });
    return () => { cancelled = true; };
    /*
     * The corridor is in here on purpose. These points are fetched over the
     * ground the route covers, so moving the start or the destination changes
     * which places are avoided — and without this dependency the map went on
     * showing the zones for wherever the route used to go.
     */
  }, [city, avoidCategories, corridor]);

  /*
   * Zones drawn while choosing, before any route exists. Individual places
   * come from state; avoided categories are resolved from the POI layer data
   * when those categories happen to be displayed.
   */
  /*
   * Resolved exactly as the server resolves it: the place's own radius, then
   * its category's, then the slider. Drawn any other way the preview would
   * disagree with the plan it is previewing.
   */
  const radiusFor = useCallback(
    (poiId: string | undefined, categoryKey: string | undefined) =>
      (poiId ? avoidDistanceByPoiId[poiId] : undefined)
      ?? (categoryKey ? avoidDistanceByCategoryKey[categoryKey] : undefined)
      ?? avoidDistanceM,
    [avoidDistanceByPoiId, avoidDistanceByCategoryKey, avoidDistanceM],
  );

  const previewAvoid = useMemo(
    () => [
      ...avoid.map((p) => ({
        lat: p.lat, lng: p.lng, radiusM: radiusFor(p.id, p.categoryKey), categoryKey: p.categoryKey,
      })),
      ...avoidCategoryPoints.map((p) => ({
        ...p, radiusM: radiusFor(undefined, p.categoryKey),
      })),
      ...avoidPins.map((p) => ({ lat: p.lat, lng: p.lng, radiusM: avoidDistanceM })),
    ],
    [avoid, avoidCategoryPoints, avoidPins, avoidDistanceM, radiusFor],
  );

  /*
   * Everything the traveller has chosen, drawn on the map before any route
   * exists.
   *
   * Places picked from the dropdowns are included as well as dropped pins:
   * choosing a start by name and seeing nothing appear gives no confirmation
   * that the right one was picked. They are marked `fromList` so that moving
   * one can be told from moving a dropped pin: the name no longer describes
   * the new position, so the place is replaced by a coordinate there.
   */
  /*
   * Whether the form currently describes a loop.
   *
   * Read from the draft rather than from a flag the form owns: the draft is
   * what actually gets planned, so this cannot disagree with the route. It
   * also covers a destination left over from before the round-trip switch was
   * turned on — the plan ignores it, so the map must not show it either.
   */
  const draftIsRoundTrip = useMemo(() => {
    if (!draft) return false;
    const { start: a, end: b } = draft;
    if (a.kind === 'poi' && b.kind === 'poi') return a.poiId === b.poiId;
    if (a.kind === 'coords' && b.kind === 'coords') {
      return Math.abs(a.lat - b.lat) < 1e-4 && Math.abs(a.lng - b.lng) < 1e-4;
    }
    return false;
  }, [draft]);
  /*
   * Whether the round has both ends yet.
   *
   * A destination counts as set when "return to the departure point" is on,
   * because that IS the destination — the same place as the start.
   */
  const hasBothEnds = Boolean(start || startPin) && Boolean(end || endPin || draftIsRoundTrip);

  const mapPins = useMemo<
    {
      lat: number; lng: number; label?: string; address?: string;
      fromList?: boolean; fixed?: boolean;
      role: 'start' | 'end' | 'visit' | 'avoid' | 'suggested';
      highlighted?: boolean; poiId?: string; onRemove?: () => void;
    }[]
  >(
    () => [
      /*
        Only the MARKED ones carry a way out.

        A start chosen from the list belongs to the list, and removing it
        from the map would leave the field on the form still holding it.
        The spots dropped by right-click have no other home, which is why
        they were the ones with no way back.
      */
      ...(startPin
        ? [{
            ...startPin,
            role: 'start' as const,
            onRemove: () => { setStartPin(null); setToast('Start removed'); },
          }]
        : start
          ? [{
              lat: start.lat, lng: start.lng, label: start.name,
              ...(start.address ? { address: start.address } : {}),
              /*
                The id, so the marker opens what is known about the place.

                Only the suggestion rings carried one, which meant the places
                somebody had actually CHOSEN were the ones they could not read
                about — and the coverage record is most worth reading before
                the round is planned, not after. See onPinClick in MapView: any
                pin carrying an id is a door.
              */
              poiId: start.id,
              role: 'start' as const, fromList: true,
            }]
          : []),
      // A loop has no separate destination to draw; the start marker is both.
      ...(draftIsRoundTrip
        ? []
        : endPin
          ? [{
              ...endPin,
              role: 'end' as const,
              onRemove: () => { setEndPin(null); setToast('Destination removed'); },
            }]
          : end
            ? [{
                lat: end.lat, lng: end.lng, label: end.name,
                ...(end.address ? { address: end.address } : {}),
                poiId: end.id,
                role: 'end' as const, fromList: true,
              }]
            : []),
      ...visitPins.map((p, i) => ({
        ...p,
        role: 'visit' as const,
        onRemove: () => {
          setVisitPins((current) => current.filter((_, n) => n !== i));
          setToast('Marked stop removed');
        },
      })),
      ...avoidPins.map((p, i) => ({
        ...p,
        role: 'avoid' as const,
        onRemove: () => {
          setAvoidPins((current) => current.filter((_, n) => n !== i));
          setToast('Marked area removed');
        },
      })),
      ...namedPlaces.map((p) => ({
        lat: p.lat, lng: p.lng, label: p.name,
        ...(p.address ? { address: p.address } : {}),
        poiId: p.id,
        role: 'visit' as const, fromList: true,
      })),
      ...avoid.map((p) => ({
        lat: p.lat, lng: p.lng, label: p.name,
        ...(p.address ? { address: p.address } : {}),
        role: 'avoid' as const, fromList: true,
      })),
      /*
       * A place chosen for a break is a place the day will visit, so it
       * belongs on the map with the others. Without this, picking lunch from
       * the suggestions filled the form and changed nothing a traveller could
       * see — the one confirmation that mattered.
       *
       * Drawn as a visit because that is what it is; the itinerary is where
       * it is later identified as the lunch stop.
       *
       * Fixed where the others are not: a break travels in the request as a
       * place id, so there is no position for a drag to move it to. It is
       * changed by choosing a different place for it.
       */
      ...[breakPlaces.lunch, breakPlaces.coffee]
        .filter((p): p is Poi => p !== null)
        .map((p) => ({
          lat: p.lat, lng: p.lng, label: p.name,
          ...(p.address ? { address: p.address } : {}),
          role: 'visit' as const, fromList: true, fixed: true,
        })),
      /*
       * Places being offered, drawn hollow so they cannot be mistaken for the
       * day as planned. Last in the list so a suggestion never draws over a
       * place that has actually been chosen — including itself, once added.
       */
      ...suggestionPreview.places
        .filter((p) => (
          !mustVisit.some((m) => m.id === p.poiId)
          // A place taken for a break is drawn solid just above, and the ring
          // would sit on top of it — two markers claiming one spot, one of
          // them saying it is not in the trip.
          && breakPlaces.lunch?.id !== p.poiId
          && breakPlaces.coffee?.id !== p.poiId
        ))
        .map((p) => ({
          lat: p.lat, lng: p.lng, label: p.name, poiId: p.poiId,
          role: 'suggested' as const, fromList: true,
          highlighted: suggestionPreview.hoveredPoiId === p.poiId,
        })),
    ]
      /*
       * Once a route exists, its numbered circles already stand for every
       * place that made it in. Leaving the pins drawn as well put a pin and a
       * circle on the same spot — two markers for one stop.
       *
       * Matched on position rather than dropped wholesale, so a place that
       * did NOT make it into the route keeps its pin and stays visible
       * instead of quietly disappearing from the map.
       */
      .filter((pin) => !(plan?.stops ?? []).some(
        (stop) => Math.abs(stop.lat - pin.lat) < 1e-5 && Math.abs(stop.lng - pin.lng) < 1e-5,
      )),
    [startPin, endPin, visitPins, avoidPins, start, end, mustVisit, avoid, draftIsRoundTrip,
     breakPlaces, suggestionPreview, plan],
  );

  /* Configured defaults, with the hardcoded values as a last resort only. */
  const breakMinutes = useMemo(
    () => ({
      lunch: breakOverride.lunch ?? Number(settings['planner.default_lunch_minutes'] ?? 45),
      coffee: breakOverride.coffee ?? Number(settings['planner.default_coffee_minutes'] ?? 20),
    }),
    [breakOverride, settings],
  );

  /*
   * What a spot marked on the map gets when it is given no stay of its own.
   *
   * A place found by name takes its category's; only a bare coordinate has
   * nothing to fall back on, which is what this setting is for. Each marked
   * spot can still be given its own in the form.
   */
  const defaultStopMinutes = Number(settings['planner.pinned_stop_dwell_minutes'] ?? 30);

  const handleBreakMinutesChange = useCallback(
    (kind: 'lunch' | 'coffee', minutes: number) => {
      if (!Number.isFinite(minutes)) return;
      setBreakOverride((current) => ({
        ...current,
        [kind]: Math.min(240, Math.max(5, Math.round(minutes))),
      }));
    },
    [],
  );

  /** One colour per category, shared by the sidebar swatches and the map. */
  const categoryColours = useMemo(
    () => buildCategoryColours(categories.map((c) => c.key)),
    [categories],
  );

  const colourFor = useCallback(
    (key: string) => categoryColours.get(key) ?? DEFAULT_GROUP_COLOUR,
    [categoryColours],
  );

  /*
    The cities a journey crosses were fetched to name them on the form, in a
    line that said every count below covered them whole. The line has gone —
    it explained a subtlety the counts never raised — and with it the only
    reader of this, so the request goes too.
  */

  /**
   * Runs a plan and shows it.
   *
   * `live` marks a re-plan triggered by an edit rather than by pressing the
   * button. Those leave the form open — closing it under someone who is still
   * adjusting things would take the panel away mid-edit — and only an explicit
   * submit disables the button, so a background re-plan never makes the form
   * look frozen.
   *
   * A failure never clears an existing route. The previous one remains the
   * most useful thing on screen while the error explains itself, and blanking
   * it also stops the live re-plan, which needs a plan to work from.
   */

  /*
   * The form has moved on from the round on the map, and is waiting to be
   * asked rather than going on its own.
   */
  const [staleForm, setStaleForm] = useState(false);

  /*
   * The plan whose break notice has been dismissed.
   *
   * Held as the plan itself rather than a flag: closing it should silence
   * THIS round, not every round after it, and a new plan is a new answer to
   * the same question.
   */
  const [breakNoticeClosed, setBreakNoticeClosed] = useState<RoutePlan | null>(null);

  /*
   * Stops taken off the request since this round was planned.
   *
   * Removing one changes what will be ASKED for, not what is drawn — the
   * route still runs through it until it is planned again. The row therefore
   * stays, struck through, rather than disappearing from a line that visibly
   * still goes there. A toast alone left the schedule looking untouched.
   *
   * Kept as positions into the drawn plan, and cleared the moment a new one
   * arrives, because that plan's rows are a different list.
   */
  const [removedStops, setRemovedStops] = useState<number[]>([]);

  /*
   * When an edit asked for a re-plan outright, rather than raising the notice.
   *
   * The form rebuilds the request a render after the state it reads changes,
   * so such an edit cannot ask for the plan directly: the draft at that
   * moment still describes the previous round. The intent is held here until
   * the new draft arrives.
   *
   * A timestamp rather than a flag, because the draft may never change — an
   * edit that lands where it started leaves the request identical — and an
   * intent left set would re-plan on some unrelated change later.
   */
  const armedReplan = useRef(0);
  const ARM_EXPIRES_MS = 5_000;

  /*
   * Which request the page is still interested in.
   *
   * A plan takes seconds; bending the path or clearing the form takes none.
   * Without this, resetting mid-flight let the answer to a question nobody
   * is asking any more arrive and redraw the route that was just cleared —
   * and a dragged via point could land its plan after the one that replaced
   * it. Every dispatch takes the current number and drops its own result if
   * the number has moved on.
   */
  const planToken = useRef(0);
  /*
   * Plans already computed, by request.
   *
   * Straightening a hand-shaped path returns to the request that produced
   * the plan before the drag — so it can be redrawn rather than waited for,
   * which on a route avoiding a whole category is the difference between
   * instant and a minute. Small and in memory: this is an undo, not a store.
   */
  const planCache = useRef(new Map<string, RoutePlan>());
  const CACHED_PLANS = 12;

  /*
   * When the form was last cleared, so an answer that was already on the wire
   * can be recognised as belonging to the trip that no longer exists.
   *
   * A moment rather than a counter: the assistant's reply carries no token of
   * its own, and anything arriving within a few seconds of a clear was asked
   * before it.
   */
  const clearedAt = useRef(0);
  const STALE_AFTER_CLEAR_MS = 120_000;

  const abandonPlans = useCallback(() => {
    planToken.current += 1;
    // Whoever started it — this form, or an emphasis beside the map.
    cancelPlan();
    // Nothing is scheduled any more: a change asks, it does not fire.
    setPlanning(false);
    setSubmitting(false);
  }, []);

  const handlePlan = useCallback(async (request: PlanRequest, live = false) => {
    /*
     * Recorded before the request, not after it succeeds. Otherwise a failed
     * request is still "not yet attempted", and every later edit re-sends it —
     * each attempt disabling the button for as long as it runs.
     */
    const token = (planToken.current += 1);
    const current = () => planToken.current === token;

    setLastAttempt(request);

    /*
     * Already planned, exactly? Then draw it.
     *
     * The case this exists for is undoing: dragging the route, disliking it,
     * and straightening it again asks for a plan that was on screen a minute
     * ago. Nothing is stale — the key IS the request, so a hit can only be
     * the answer to this question.
     */
    const key = requestSignature(request);
    const cached = planCache.current.get(key);
    if (cached) {
      setPlan(cached);
      setLastRequest(request);
      /*
       * A new day ends the preview of another one.
       *
       * Applying a themed alternative is a preview: `appliedTheme` marks it
       * in the list and `beforeTheme` is the way back to what it replaced.
       * Neither survives a re-plan — and a re-plan happens on its own, 600ms
       * after any edit — so leaving them set meant a day planned for a
       * changed time budget still announced itself as the emphasis' route,
       * and still offered a way "back" to a route built for the OLD budget.
       * Restoring it put a day nobody asked for back on the map.
       *
       * Cleared beside `appliedVariant` because it is the same fact about
       * the same moment: what is drawn now came from the form, not from
       * something picked off a list beside it.
       */
      setAppliedVariant(null);
      setAppliedTheme(null);
      setBeforeTheme(null);
      /*
       * And nothing is pending removal, for the same reason the network
       * path below says so.
       *
       * This was missing here, and removing a stop is the edit most likely
       * to HIT the cache: the round without it was on screen a moment
       * earlier. The struck-through row then survived onto a plan whose
       * rows are a different list — take the one mid stop off a three-stop
       * day and the DESTINATION came back marked "Removed".
       */
      setRemovedStops([]);
      setError(null);
      if (!live) { setEditing(false); setPanelOnMobile(false); }

      /*
       * The spinner belongs to whoever answered, and this answered.
       *
       * Taking the token above cancels whatever was in flight, and that
       * request's own `finally` then declines to clear the flag because it is
       * no longer current — rightly, since a superseded request must not
       * switch off a spinner that belongs to a later one. But this path
       * returns before the flag is ever cleared by anybody. Removing a stop
       * while a plan was still running therefore drew the cached route
       * immediately and left the bar turning for good.
       */
      setPlanning(false);
      if (!live) setSubmitting(false);

      return;
    }

    setPlanning(true);
    if (!live) setSubmitting(true);
    setError(null);
    try {
      const result = await api.planLatest(request);
      if (!current()) return;

      // A new round, so nothing is pending removal from the old one.
      setRemovedStops([]);
      planCache.current.set(key, result);
      // Oldest out first: a Map keeps insertion order, so this is the one
      // nobody has asked for in the longest time.
      if (planCache.current.size > CACHED_PLANS) {
        planCache.current.delete(planCache.current.keys().next().value as string);
      }
      setPlan(result);
      setLastRequest(request);
      // The preview of another day is over — see the cached path above.
      setAppliedVariant(null);
      setAppliedTheme(null);
      setBeforeTheme(null);
      if (!live) {
        setEditing(false);
        // On a narrow screen the point of pressing the button is to see the
        // route, so the map comes forward rather than the panel staying put.
        setPanelOnMobile(false);
      }
    } catch (err) {
      // A cancelled request has no error worth showing: nobody is waiting
      // for it, and the form it described is gone.
      const abandoned = err instanceof DOMException && err.name === 'AbortError';
      if (current() && !abandoned) setError(err as ApiError);
    } finally {
      if (current()) {
        setPlanning(false);
        if (!live) setSubmitting(false);
      }
    }
  }, []);

  /*
   * Once a route exists, any change to the form marks it out of date.
   *
   * The whole form is compared, not just the place lists: changing the travel
   * mode, the start time or a break all leave the drawn route out of date,
   * and the comparison is against the request that PRODUCED it, so the notice
   * clears itself the moment the form agrees with the map again — including
   * when an edit is undone.
   */
  useEffect(() => {
    // A plan must exist — this watches a route that is already drawn — and
    // the comparison is against what was last SENT, so a failed attempt is
    // offered again when something changes and not before.
    if (!plan || !lastAttempt || !draft) return;

    /*
     * Compared as JSON, which is enough here because the draft is rebuilt in
     * a fixed field order every time. Pin labels are stripped first: a name
     * arriving from the lookup after a pin is placed is not a change worth
     * asking about.
     *
     * Written defensively because not every request compared here was built
     * by this form: the assistant hands back a partial one, which may carry
     * no `avoid` section at all, and reading through it threw where the
     * comparison should simply have said "different".
     */
    const stale = requestSignature(draft) !== requestSignature(lastAttempt);

    // A re-plan asked for outright, now that the draft has caught up with it
    // — see `armedReplan`. Cleared first, so it is honoured exactly once.
    if (stale && Date.now() - armedReplan.current < ARM_EXPIRES_MS) {
      armedReplan.current = 0;
      setStaleForm(false);
      void handlePlan(draft, true);
      return;
    }

    setStaleForm(stale);
  }, [draft, plan, lastAttempt, handlePlan]);

  /*
   * Saving asks for a name first.
   *
   * It used to save silently under a generated name, or — when anything it
   * needed was missing — do nothing at all with no message, which is
   * indistinguishable from a save that failed. Naming it also makes the list
   * of saved trips worth reading.
   */
  const [saveName, setSaveName] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /*
   * Which promises this plan did not keep, and whether that has been read.
   *
   * Dismissed per plan rather than for good: the next route is a different
   * route, and it may fail differently.
   */
  const shortfall = useMemo(
    () => (plan?.warnings ?? []).filter((warning) => SHORTFALL.test(warning)),
    [plan],
  );
  /*
   * Which roads the drawn route actually runs on.
   *
   * "Motorways: Avoid" is a promise about the road, and the only place it was
   * answerable was the shape of a line — an arterial and a motorway are drawn
   * alike on the basemap, so the honest answer and the broken one look the
   * same. The planner already says it; this puts it where the route is.
   */
  /*
   * Read from the plan's own field, not picked out of its prose.
   *
   * This matched warnings against a pattern of opening words — three of the
   * dozen sentences the planner can produce. "No toll road is used on this
   * route" failed the test, so a toll preference that had worked perfectly
   * showed nothing at all, and the control looked broken.
   *
   * And every note now, not the first: a journey answering two road controls
   * has two things to say about itself. The planner no longer volunteers the
   * roads a route happens to use, so what arrives here is an answer to a
   * control somebody set.
   */
  const roadNotes = useMemo(() => plan?.roadNotes ?? [], [plan]);

  /*
   * The places behind whichever characteristics are set, along the drawn line.
   *
   * A slider that picks between routes cannot be seen working: two ways round
   * a city look alike whatever decided between them, so "Scenic 1.00" was a
   * number with nothing to show for it. These are the parks it counted — or
   * the bars Quiet counted against — narrowed to the same corridor the
   * planner scores within.
   *
   * Read from lastRequest rather than the form, so what is drawn belongs to
   * the plan on screen rather than to edits made since.
   */
  const [characterPlaces, setCharacterPlaces] = useState<unknown>(null);

  useEffect(() => {
    const set = Object.entries(lastRequest?.routeCharacteristics ?? {})
      .filter(([, weight]) => (weight ?? 0) > 0)
      .map(([name]) => name);

    const wanted = set.flatMap((name) => CHARACTER_CATEGORIES[name] ?? []);
    // Which sense each category was asked about, so the map can tell a place
    // the route wanted from one it wanted away from.
    const senseByCategory = Object.fromEntries(
      set.flatMap((name) => (CHARACTER_CATEGORIES[name] ?? [])
        .map((key) => [key, CHARACTER_SENSE[name] ?? 'seek'])),
    ) as Record<string, 'seek' | 'avoid'>;

    if (!plan || !city || wanted.length === 0) {
      setCharacterPlaces(null);

      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const collection = await api.poisGeoJson({
          cityId: city.id,
          categoryKeys: [...new Set(wanted)],
        });
        if (cancelled) return;

        const line = plan.legs.flatMap((leg) => leg.geometry.map(
          ([lng, lat]) => ({ lat, lng }),
        ));
        setCharacterPlaces(placesAlongRoute(collection, line, senseByCategory));
      } catch {
        // Evidence for a score is not worth an error message of its own: the
        // plan is still correct without it.
        if (!cancelled) setCharacterPlaces(null);
      }
    })();

    return () => { cancelled = true; };
  }, [plan, city, lastRequest]);
  const [shortfallDismissed, setShortfallDismissed] = useState(false);
  useEffect(() => { setShortfallDismissed(false); }, [plan]);
  /** Whether the road note has been put down for the plan on screen. */
  const [roadNoteHidden, setRoadNoteHidden] = useState(false);
  useEffect(() => { setRoadNoteHidden(false); }, [plan]);

  const openSave = useCallback(() => {
    if (!plan || !lastRequest || !city) {
      setToast('Nothing to save yet — plan a route first');
      return;
    }
    const first = plan.stops[0]?.name ?? 'Route';
    const last = plan.stops[plan.stops.length - 1]?.name ?? '';
    setSaveName(first === last ? `Round trip from ${first}` : `${first} → ${last}`);
  }, [plan, lastRequest, city]);

  const handleSave = useCallback(async () => {
    if (!plan || !lastRequest || !city || !saveName?.trim()) return;
    setSaving(true);
    try {
      await api.saveRoute({
        cityId: city.id,
        name: saveName.trim(),
        request: lastRequest,
        plan,
      });
      setSaveName(null);
      setToast('Trip saved — find it under Saved');
    } catch (err) {
      // Kept open on failure so the name is not lost and it can be retried.
      setToast(`Could not save: ${(err as ApiError).message}`);
    } finally {
      setSaving(false);
    }
  }, [plan, lastRequest, city, saveName]);

  /**
   * An external search result becomes a pinned coordinate.
   *
   * It has no place in our database — and deliberately never will, since the
   * provider's terms restrict caching — so it joins the plan the same way a
   * dropped pin does.
   */
  const handleExternalPick = useCallback(
    (
      place: { name: string; address?: string | null; lat: number; lng: number },
      role: 'start' | 'end' | 'visit' | 'avoid',
    ) => {
      // The provider already returned the address with the search result, so
      // carrying it costs nothing and saves a second lookup.
      const pin: PinnedPoint = {
        lat: place.lat,
        lng: place.lng,
        label: place.name,
        ...(place.address ? { address: place.address } : {}),
      };
      if (role === 'start') { setStartPin(pin); setStart(null); }
      else if (role === 'end') { setEndPin(pin); setEnd(null); }
      else if (role === 'visit') setVisitPins((c) => [...c, pin]);
      else setAvoidPins((c) => [...c, pin]);
      setToast(`${place.name} added`);
    },
    [],
  );

  /**
   * Gives a spot marked on the map one of the four roles.
   *
   * The pin is placed immediately with a coordinate label and the name is
   * filled in when the lookup answers. Waiting for the network before the pin
   * appears would make marking a spot feel broken, and the name is a nicety —
   * the coordinate is what actually routes.
   */
  /*
   * Bend the drawn route through the marked spot.
   *
   * A via point rather than a stop, on the same grounds as dragging the line:
   * "go this way" is not "take me here", and a stop would earn a dwell time
   * and a number in the itinerary nobody asked for.
   *
   * Offered from the right-click dialog because dragging is fiddly — the
   * handle has to be caught on the line and let go exactly where it is
   * wanted, and a drop a few pixels out puts the point somewhere else. Right-
   * click names the spot precisely, which is the whole reason this dialog
   * exists for the other four roles.
   *
   * Which leg to bend is measured rather than asked: the nearest to the spot,
   * so a point beside the third leg shapes the third leg. Dragging gets that
   * free from the line it was grabbed on; a click anywhere does not.
   */
  const routeThroughSpot = useCallback(() => {
    if (!markedPoint || !plan) return;
    const point = markedPoint;
    setMarkedPoint(null);

    let nearestLeg = 0;
    let nearest = Infinity;
    plan.legs.forEach((leg, index) => {
      for (const [lng, lat] of leg.geometry) {
        // Squared degrees, longitude narrowed by latitude. Ranking only, so
        // the cost of a true distance buys nothing here.
        const dx = (lng - point.lng) * Math.cos((point.lat * Math.PI) / 180);
        const dy = lat - point.lat;
        const d = dx * dx + dy * dy;
        if (d < nearest) { nearest = d; nearestLeg = index; }
      }
    });

    setViaPoints((current) => [...current, { ...point, afterStopIndex: nearestLeg }]);
    setToast('Path routed through the marked spot');
  }, [markedPoint, plan]);

  /*
   * The route, redrawn by hand.
   *
   * The same via point "route through here" places from a marked spot, but
   * arrived at by dragging the line itself, which is how every map people
   * already use lets them do this. The leg comes from the handle rather than
   * from a nearest-point search: the gesture started on a known leg, and
   * working it out afterwards puts the point on the wrong one wherever a
   * round doubles back on itself.
   *
   * Re-planned at once. A drag is a finished gesture, and a line that waits
   * to be told again is a line that did not do what it was dragged to do.
   */
  const bendRoute = useCallback(
    (point: { lat: number; lng: number }, afterStopIndex: number) => {
      setViaPoints((current) => [...current, { ...point, afterStopIndex }]);
      armedReplan.current = Date.now();
      setToast('Route redrawn through the point');
    },
    [],
  );

  /*
   * A shaping point, moved rather than removed and placed again.
   *
   * The leg it belongs to does not change: a point dragged a street over is
   * still shaping the same stretch, and re-deciding that from the new
   * position would move the bend to a different part of the round whenever
   * two legs run close together.
   */
  const moveViaPoint = useCallback(
    (index: number, point: { lat: number; lng: number }) => {
      setViaPoints((current) => current.map(
        (via, i) => (i === index ? { ...via, ...point } : via),
      ));
      armedReplan.current = Date.now();
    },
    [],
  );

  const markSpot = useCallback(
    (role: 'start' | 'end' | 'visit' | 'avoid') => {
      if (!markedPoint) return;
      const point = markedPoint;
      const pin: PinnedPoint = {
        ...point,
        label: `${point.lat.toFixed(5)}, ${point.lng.toFixed(5)}`,
      };
      setMarkedPoint(null);

      if (role === 'start') { setStartPin(pin); setStart(null); setToast('Start set to the marked spot'); }
      else if (role === 'end') { setEndPin(pin); setEnd(null); setToast('Destination set to the marked spot'); }
      else if (role === 'visit') {
        setVisitPins((c) => [...c, pin]);
        unpinOrder();
        setToast('Stop added');
      }
      else { setAvoidPins((c) => [...c, pin]); setToast('Avoiding the marked area'); }

      if (!city) return;
      void api.describePlace(city.id, point.lat, point.lng)
        .then((found) => {
          if (!found.name) return;
          const named: PinnedPoint = {
            ...pin,
            label: found.name,
            ...(found.address ? { address: found.address } : {}),
          };
          // Matched on coordinates: the pin may have been moved or removed
          // while the lookup was in flight.
          const same = (p: PinnedPoint) => p.lat === point.lat && p.lng === point.lng;
          if (role === 'start') setStartPin((c) => (c && same(c) ? named : c));
          else if (role === 'end') setEndPin((c) => (c && same(c) ? named : c));
          else if (role === 'visit') setVisitPins((c) => c.map((p) => (same(p) ? named : p)));
          else setAvoidPins((c) => c.map((p) => (same(p) ? named : p)));
        })
        .catch(() => { /* an unnamed pin still routes */ });
    },
    [markedPoint, city],
  );


  /*
   * Which stop the pinned-stop panel is describing, by position in the round.
   *
   * Held as an index rather than the stop itself so a re-plan cannot leave the
   * panel showing times the round no longer keeps.
   */
  const [pinIndex, setPinIndex] = useState<number | null>(null);

  const showPinnedStop = useCallback((index: number) => {
    setHighlighted(index);
    setPinIndex(index);
  }, []);

  /** Clicking a via point removes it, straightening the path back out. */
  const handleViaPointClick = useCallback((index: number) => {
    setViaPoints((current) => current.filter((_, i) => i !== index));
    setToast('Via point removed');
  }, []);

  /*
   * Moving a pin on the map.
   *
   * Resolved against `mapPins` rather than by counting positions, so adding
   * another kind of marker to that list cannot silently start moving the
   * wrong pin. Places chosen from a list are not draggable and are ignored
   * here as a second line of defence.
   */
  const handlePinDrag = useCallback(
    (index: number, point: { lat: number; lng: number }) => {
      const target = mapPins[index];
      if (!target || target.fixed || target.role === 'suggested') return;

      const wasAt = { lat: target.lat, lng: target.lng };
      const isTarget = (p: { lat: number; lng: number }) =>
        p.lat === wasAt.lat && p.lng === wasAt.lng;
      const provisional = `${point.lat.toFixed(5)}, ${point.lng.toFixed(5)}`;
      // The old name described the old position, so it goes with it.
      const move = (p: PinnedPoint): PinnedPoint =>
        isTarget(p) ? { ...p, ...point, label: provisional, address: undefined } : p;

      const { role } = target;

      if (target.fromList) {
        /*
         * A place chosen by name, moved off it.
         *
         * Nothing at the new position answers to that name, so the choice is
         * given up and a bare position takes its place — in the same role,
         * which is the part the traveller meant. The place's own stay time
         * is keyed by its id and goes with it; the pin takes the default for
         * a dropped spot until it is given one.
         */
        const landed: PinnedPoint = { ...point, label: provisional };
        if (role === 'start') { setStart(null); setStartPin(landed); }
        else if (role === 'end') { setEnd(null); setEndPin(landed); }
        else if (role === 'visit') {
          setMustVisit((c) => c.filter((p) => !isTarget(p)));
          setVisitPins((c) => [...c, landed]);
          // An order naming the place that has just gone no longer describes
          // the round.
          setPinnedOrder(null);
        } else {
          setAvoid((c) => c.filter((p) => !isTarget(p)));
          setAvoidPins((c) => [...c, landed]);
        }
      } else if (role === 'start') setStartPin((c) => (c ? move(c) : c));
      else if (role === 'end') setEndPin((c) => (c ? move(c) : c));
      else if (role === 'visit') setVisitPins((c) => c.map(move));
      else setAvoidPins((c) => c.map(move));

      // Moving anything on the map changes the round it describes, and a drag
      // is a finished gesture — so it asks for the new one. See `armedReplan`.
      armedReplan.current = Date.now();

      if (!city) return;
      void api.describePlace(city.id, point.lat, point.lng)
        .then((found) => {
          if (!found.name) return;
          const name = (p: PinnedPoint): PinnedPoint =>
            p.lat === point.lat && p.lng === point.lng
              ? { ...p, label: found.name!, ...(found.address ? { address: found.address } : {}) }
              : p;
          if (role === 'start') setStartPin((c) => (c ? name(c) : c));
          else if (role === 'end') setEndPin((c) => (c ? name(c) : c));
          else if (role === 'visit') setVisitPins((c) => c.map(name));
          else setAvoidPins((c) => c.map(name));
        })
        .catch(() => { /* position is what matters; the name is a nicety */ });
    },
    [city, mapPins],
  );

  /*
   * Moving an end of a route that is already planned.
   *
   * Separate from `handlePinDrag`: these markers come from the plan rather
   * than from the pin list. A place chosen by name no longer describes the
   * dropped position, so it is replaced by a pinned coordinate there.
   *
   * The new round is then asked for directly rather than through the "plan
   * again" notice, a drag being a completed gesture rather than an edit
   * still in progress.
   */
  const handleEndpointDrag = useCallback(
    (kind: 'start' | 'end', point: { lat: number; lng: number }) => {
      // The old name described the old position, so it goes with it.
      const label = `${point.lat.toFixed(5)}, ${point.lng.toFixed(5)}`;
      // A stay time set by hand follows the end to its new position.
      const moved = (current: PinnedPoint | null): PinnedPoint => ({
        ...point,
        label,
        ...(current?.dwellMinutes !== undefined ? { dwellMinutes: current.dwellMinutes } : {}),
      });

      if (kind === 'start') { setStart(null); setStartPin(moved); }
      else { setEnd(null); setEndPin(moved); }

      /*
       * Move the drawn marker to the drop, ahead of the new route.
       *
       * The marker is drawn from the plan, so without this it returns to its
       * old position for as long as the re-plan takes. The line is left as it
       * is: it still describes the previous route, which the new plan
       * replaces.
       */
      setPlan((current) => (current ? {
        ...current,
        stops: current.stops.map((stop) => (
          // A loop's two ends are one place, so both follow the drag.
          stop.kind === kind
          || (draftIsRoundTrip && (stop.kind === 'start' || stop.kind === 'end'))
            ? { ...stop, ...point, name: label, poiId: null, categoryKey: null }
            : stop
        )),
      } : current));

      armedReplan.current = Date.now();
      setToast(kind === 'start' ? 'Start moved — re-planning' : 'Destination moved — re-planning');

      if (!city) return;
      void api.describePlace(city.id, point.lat, point.lng)
        .then((found) => {
          if (!found.name) return;
          // Only if it is still where it was dropped: the drag may have been
          // repeated while this was in flight.
          const name = (p: PinnedPoint | null): PinnedPoint | null =>
            p && p.lat === point.lat && p.lng === point.lng
              ? { ...p, label: found.name!, ...(found.address ? { address: found.address } : {}) }
              : p;
          if (kind === 'start') setStartPin(name);
          else setEndPin(name);
        })
        .catch(() => { /* position is what matters; the name is a nicety */ });
    },
    [city, draftIsRoundTrip],
  );

  /*
   * Reordering the itinerary by hand.
   *
   * Moving one stop pins the whole itinerary: every stop currently shown
   * becomes an explicit request in the new order, and the optimiser is told
   * to leave it alone. Anything the planner had suggested is promoted to a
   * real stop in the process, so nothing the traveller can see disappears
   * from under them.
   *
   * State is updated rather than a request sent directly, so the arrangement
   * is carried into every later re-plan instead of lasting only until the
   * next edit.
   */
  const handleReorder = useCallback(
    async (fromIndex: number, toIndex: number) => {
      if (!plan) return;

      const middle = plan.stops.slice(1, -1);
      const from = fromIndex - 1;
      const to = toIndex - 1;
      if (from < 0 || to < 0 || from >= middle.length || to >= middle.length) return;

      const reordered = [...middle];
      const [moved] = reordered.splice(from, 1);
      if (!moved) return;
      reordered.splice(to, 0, moved);

      // Pin ids must match the order the coordinates are sent in, since the
      // server addresses them by position.
      let pinIndex = 0;
      const order = reordered.map((stop) => stop.poiId ?? `pin-${pinIndex++}`);

      /*
       * Suggested stops are not in `mustVisit` yet — they were the planner's
       * idea, not the traveller's. Pinning the order makes every visible stop
       * explicit, so the ones we do not already hold are fetched.
       */
      const known = new Map(mustVisit.map((poi) => [poi.id, poi]));
      const wanted = reordered
        .map((stop) => stop.poiId)
        .filter((id): id is string => Boolean(id));

      try {
        const fetched = await Promise.all(
          wanted.map(async (id) => known.get(id) ?? await api.poi(id)),
        );
        setMustVisit(fetched);
        /* Anything not already named is here to hold the order, not by choice. */
        setPinnedByOrder((current) => {
          const next = new Set(current);
          const chosen = new Set(mustVisit.map((p) => p.id));
          for (const id of wanted) if (!chosen.has(id)) next.add(id);

          return next;
        });
      } catch {
        setToast('Could not pin the order — a place could not be loaded');
        return;
      }

      setVisitPins(
        reordered
          .filter((stop) => !stop.poiId)
          .map((stop) => ({
            lat: stop.lat, lng: stop.lng, label: stop.name,
            dwellMinutes: stop.dwellMinutes,
          })),
      );

      /*
       * The stays move with the order.
       *
       * Pinning an order makes every stop one the traveller asked for by
       * name, and a place asked for by name keeps its own stay rather than
       * its kind's. Without the stays written down, a round built from kinds
       * set to pass by came back from a drag with every one of them stopped
       * at for its usual forty-five minutes — the same round, re-read as
       * a hundred and thirty-five visits.
       *
       * So what the round currently does is recorded before it is re-planned:
       * nought stays nought, ninety stays ninety, and dragging one row
       * changes the order and nothing else.
       */
      setStopDwellMinutes((current) => {
        const next = { ...current };
        for (const stop of reordered) {
          if (stop.poiId) next[stop.poiId] = stop.dwellMinutes;
        }

        return next;
      });
      setPinnedOrder(order);

      /*
       * Re-planned without being asked, unlike a form edit.
       *
       * A new order is an instruction about the route in front of you, not a
       * setting: leaving it behind the button would leave the rows in one
       * order and the line on the map in another until it was pressed.
       */
      armedReplan.current = Date.now();
      setToast('Order pinned — re-planning');
    },
    [plan, mustVisit],
  );

  /*
   * Changing how long to stay at a stop.
   *
   * Breaks are not places in the itinerary the way a visit is — they are a
   * request for "somewhere to eat around one o'clock" — so their length is
   * the break's own duration rather than an override against whichever café
   * the planner happened to pick. Everything else is keyed by place id, or
   * by pin position for a dropped coordinate.
   *
   * The stay feeds the optimiser as well as the printed schedule, so a longer
   * one can genuinely push a later stop out of the time budget. That is the
   * intended behaviour: the plan should say so rather than quietly overrun.
   */
  const handleDwellChange = useCallback(
    (index: number, minutes: number) => {
      if (!plan) return;
      const stop = plan.stops[index];
      if (!stop) return;

      if (stop.kind === 'lunch') {
        setBreakOverride((current) => ({ ...current, lunch: minutes }));
        return;
      }
      if (stop.kind === 'coffee') {
        setBreakOverride((current) => ({ ...current, coffee: minutes }));
        return;
      }

      // Pin keys count only the coordinate stops, in itinerary order, which
      // is how the server addresses them.
      let key = stop.poiId;
      if (!key) {
        const pinIndex = plan.stops
          .slice(1, index)
          .filter((s) => s.kind === 'visit' && !s.poiId).length;
        key = `pin-${pinIndex}`;
      }
      setStopDwellMinutes((current) => ({ ...current, [key!]: minutes }));
    },
    [plan],
  );

  /*
   * Removing a stop from the itinerary.
   *
   * What removal means depends on where the stop came from. A break is a
   * standing request for "somewhere to eat around one", so it is switched
   * off rather than excluded — otherwise the planner would just pick a
   * different café. A place the traveller asked for is taken off its list. A
   * suggestion has no list to be taken off, so it is remembered as unwanted.
   */
  /*
   * Where this place sits in the round, when it can be dropped from it.
   *
   * Only the middle of the round: the start and the end are the journey
   * rather than stops on it, and dropping one would leave a round with no
   * departure. -1 when the open place is not in the round at all, or is
   * already struck through awaiting the next plan.
   */
  const droppableStop = useMemo(() => {
    if (!detailsPoiId || !plan) return -1;
    const at = plan.stops.findIndex((stop) => stop.poiId === detailsPoiId);
    if (at <= 0 || at >= plan.stops.length - 1) return -1;

    return removedStops.includes(at) ? -1 : at;
  }, [detailsPoiId, plan, removedStops]);

  const handleRemoveStop = useCallback(
    (index: number) => {
      const stop = plan?.stops[index];
      if (!stop) return;

      setRemovedStops((current) => (
        current.includes(index) ? current : [...current, index]
      ));

      if (stop.kind === 'lunch' || stop.kind === 'coffee') {
        setBreaksEnabled((current) => ({ ...current, [stop.kind]: false }));
        // The chosen place goes with the break; switching it back on should
        // not silently restore a choice the traveller just removed.
        setBreakPlaces((current) => ({ ...current, [stop.kind]: null }));
        armedReplan.current = Date.now();
        setToast(
          `${stop.kind === 'lunch' ? 'Lunch' : 'Coffee'} stop removed — re-planning`,
        );

        return;
      }

      if (stop.poiId) {
        const wasRequested = mustVisit.some((poi) => poi.id === stop.poiId);
        setMustVisit((current) => current.filter((poi) => poi.id !== stop.poiId));
        if (!wasRequested) {
          setExcludedPoiIds((current) =>
            current.includes(stop.poiId!) ? current : [...current, stop.poiId!]);
        }
      } else {
        // A pinned coordinate, matched by position — it has no id.
        setVisitPins((current) => current.filter(
          (pin) => Math.abs(pin.lat - stop.lat) > 1e-5 || Math.abs(pin.lng - stop.lng) > 1e-5,
        ));
      }

      // A pinned order naming the removed stop no longer describes the route.
      setPinnedOrder(null);
      // Taking a stop off the round re-plans on its own, for the same reason
      // a reorder does — see `handleReorder`.
      armedReplan.current = Date.now();
      setToast(`${stop.name} removed — re-planning`);
    },
    [plan, mustVisit],
  );

  /*
   * Back to an automatically planned route.
   *
   * Drops the hand-arranged order and the shaping points, but keeps the
   * places: the intent is "work it out for me again", not "start over".
   */
  const handleResetToAuto = useCallback(() => {
    /*
     * Anything already routing was routing the shaped path, and the shaping
     * is what this removes. Dropped first; clearing the via points changes
     * the draft, which arms a fresh re-plan of the straightened route.
     */
    abandonPlans();
    setPinnedOrder(null);
    setViaPoints([]);
    setToast('Back to an automatically planned route');
  }, [abandonPlans]);

  /*
   * Start over completely.
   *
   * The form owns fields this page never sees — style, mode, times,
   * preferences — so it is remounted by changing its key rather than reaching
   * in to reset each one.
   */
  const handleResetForm = useCallback(() => {
    // First, so nothing already on the wire can redraw what is about to be
    // cleared — including a re-plan a path change queued a moment ago, and
    // an assistant turn that has been thinking for half a minute.
    abandonPlans();
    clearedAt.current = Date.now();
    setPlan(null);
    setLastRequest(null);
    setLastAttempt(null);
    setError(null);
    setStart(null);
    setEnd(null);
    setStartPin(null);
    setEndPin(null);
    setMustVisit([]);
    setPinnedByOrder(new Set());
    setAvoid([]);
    setAvoidCategories([]);
    setVisitPins([]);
    setAvoidPins([]);
    setViaPoints([]);
    setPinnedOrder(null);
    setStopDwellMinutes({});
    setBreakOverride({});
    setBreaksEnabled({ lunch: false, coffee: false });
    setBreakPlaces({ lunch: null, coffee: null });
    setExcludedPoiIds([]);
    setAppliedVariant(null);
    setAvoidDistanceByPoiId({});
    setAvoidDistanceByCategoryKey({});
    setLegModes({});
    /*
     * The drawn zones, and everything the assistant left behind.
     *
     * Clearing the chips is not clearing the map: the avoided places are
     * held separately so they can be drawn before a plan exists, and a
     * cleared form that still showed forty green circles was telling the
     * traveller their avoidance survived. `seed` goes with them — it is the
     * request the form is rebuilt from, so leaving it would restore the mode,
     * style, date and breaks a moment after they were cleared.
     */
    setAvoidCategoryPoints([]);
    // The cache is an undo for the day being planned; a cleared form has no
    // day to undo to.
    planCache.current.clear();
    setSeed(null);
    setInterestKeys([]);
    setSuggestionPreview({ places: [], hoveredPoiId: null, role: 'visit' });
    setCoverage(null);
    setRegions(null);
    setAppliedTheme(null);
    setDraft(null);
    setEditing(false);
    setHighlighted(null);
    setFormKey((key) => key + 1);
    setToast('Form cleared');
  }, [abandonPlans]);

  /*
   * Choosing one of the offered alternatives.
   *
   * The engine has no "use route B" parameter, so the choice is expressed
   * the only way it understands: a stop on the chosen line. Its midpoint is
   * far enough from the others to pull the route across, and the result is a
   * normal plan that can then be edited like any other.
   */
  /*
   * Every way round this journey, in a fixed order, and which one is drawn.
   *
   * The panel used to show a "Current" row built from the plan, and the
   * alternatives beneath it. Choosing one therefore moved its numbers up into
   * Current and pushed the drawn one down into the slot it had left — the
   * rows stayed put and their contents traded places, which reads as the list
   * having been reordered under the cursor.
   *
   * So the list is built once, when a plan arrives, and never changes. Only
   * `current` moves.
   *
   * Rebuilt on `plan.alternatives` identity rather than on `plan`: switching
   * rewrites the legs and the totals but leaves that array exactly as the
   * server sent it, so this fires for a new plan and not for a switch.
   */
  const [ways, setWays] = useState<{ list: RouteAlternative[]; current: number } | null>(null);

  useEffect(() => {
    const leg = plan?.legs[0];
    if (!plan || !leg || plan.legs.length !== 1 || plan.alternatives.length === 0) {
      setWays(null);

      return;
    }

    setWays({
      // The drawn line first, because it is the one that was planned; the
      // alternatives follow in the order the engine offered them.
      list: [
        {
          index: 0,
          distanceMeters: leg.distanceMeters,
          durationMinutes: leg.durationMinutes,
          geometry: leg.geometry,
          roadAnswer: plan.roadAnswer ?? '',
        },
        ...plan.alternatives,
      ],
      current: 0,
    });
    // Keyed on the alternatives array, for the reason above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan?.alternatives]);

  /*
   * The lines the map may draw, keyed by their position in the list.
   *
   * Memoised, and that is not a tidiness point. `altData` inside the map is
   * built from this array's identity, and the layer's source is re-sent
   * whenever it changes. Built inline in the JSX it was a new array on every
   * render, so pointing at a row re-sent the whole source AND set the filter
   * in the same tick — the filter landed against data that was still being
   * re-parsed, and the line only appeared on the second hover. Held still, a
   * hover touches nothing but the filter, which is what it did before any of
   * this and what it should do.
   *
   * Keyed on the LIST, not on `ways`: switching makes a new wrapper object
   * but the same list, and the set of lines the map may draw is the same
   * before and after a switch. Only a new plan changes it.
   */
  /*
   * Breaks that were asked for and are not on the round that is drawn.
   *
   * Compared against the request that PRODUCED this plan, not against the
   * form: a break switched on a moment ago has not been planned for yet, and
   * calling it missing would accuse the planner of dropping something it has
   * not been asked about. `lastRequest` is what the drawn round answers.
   */
  const breakMissed = useMemo(() => {
    const has = (kind: string) => Boolean(plan?.stops.some((s) => s.kind === kind));

    return {
      lunch: Boolean(lastRequest?.lunch) && Boolean(plan) && !has('lunch'),
      coffee: Boolean(lastRequest?.coffee) && Boolean(plan) && !has('coffee'),
    };
  }, [plan, lastRequest]);

  const showBreakNotice = Boolean(plan)
    && (breakMissed.lunch || breakMissed.coffee)
    && breakNoticeClosed !== plan;

  /*
   * Which break, when it was wanted, and when the round actually runs.
   *
   * The times are the whole point. "Could not be placed" invites the reading
   * that the planner failed; "asked for at 1:00 PM, and this round runs 9:00
   * to 11:02" is the same fact with the mismatch visible, and the reader
   * fixes it without being told how.
   *
   * Stated side by side rather than diagnosed. A window inside the round that
   * found nothing open is the same sentence, and claiming it fell outside
   * would be wrong half the time.
   */
  const breakTimes = useMemo(() => {
    const asked = (spec?: { targetMinutesFromStart: number } | null) => (
      spec && plan ? clockAfter(plan.startTime, spec.targetMinutesFromStart) : null
    );

    return {
      lunch: asked(lastRequest?.lunch),
      coffee: asked(lastRequest?.coffee),
      ends: plan?.stops[plan.stops.length - 1]?.arrivalTime ?? null,
    };
  }, [plan, lastRequest]);

  /*
   * Whether the break was due after the round had already finished.
   *
   * This is the one case worth diagnosing, and the only one that can be told
   * apart with certainty: a time past the end could never have been met, and
   * the time is the thing to change. A break due INSIDE the round failed for
   * some other reason — nothing open near there, or no room left once the
   * rest of the day was placed — and saying "the round finishes before then"
   * of a 9:30 break on a round running to 9:54 reads as a planner that cannot
   * tell the time.
   */
  /*
   * The place found for the break was cut off by the exclusions.
   *
   * The commonest cause once a round keeps clear of a dense category, and the
   * one the other two sentences read worst against: told that nothing is open
   * or that there is no room, somebody goes and changes a time that was never
   * the problem. The planner reports it on the dropped row; this reads it
   * back rather than guessing.
   */
  const breakWalledOff = (plan?.dropped ?? []).some(
    (d) => d.reason.startsWith('No road'),
  );

  const ranLate = (kind: 'lunch' | 'coffee') => {
    const spec = lastRequest?.[kind];

    // "Too late for the round" is only a thing to say about an hour somebody
    // chose. Left on any time, there is no hour to be late.
    return Boolean(spec) && !spec?.anytime
      && (spec?.targetMinutesFromStart ?? 0) > (plan?.totals.totalMinutes ?? 0);
  };

  const breakRanLate = Boolean(plan) && (
    (breakMissed.lunch && ranLate('lunch')) || (breakMissed.coffee && ranLate('coffee'))
  );

  /*
   * Named, with its hour only when an hour was asked for.
   *
   * A break left on "any time" has no target the traveller chose, and quoting
   * the field's leftover value back at them — "asked for at 4:00 PM" — both
   * invents a request and contradicts what the planner says about the same
   * break. The hour appears when it was pinned and not otherwise.
   */
  const askedAt = (kind: 'lunch' | 'coffee') => {
    const spec = lastRequest?.[kind];
    if (!spec || spec.anytime) return '';

    return `, asked for at ${formatClock(breakTimes[kind] ?? '')},`;
  };

  const missedBreakLabel = breakMissed.lunch && breakMissed.coffee
    ? 'Your lunch stop and coffee break are not on this round.'
    : breakMissed.lunch
      ? `Your lunch stop${askedAt('lunch')} is not on this round.`
      : `Your coffee break${askedAt('coffee')} is not on this round.`;

  const roundRuns = plan && breakTimes.ends
    ? `It runs ${formatClock(plan.startTime)} to ${formatClock(breakTimes.ends)}. `
    : '';

  const altLines = useMemo(
    () => ways?.list.map((way, at) => ({ index: at, geometry: way.geometry })),
    [ways?.list],
  );

  /**
   * Switching to another way round, without planning anything.
   *
   * This used to drop a via point on the chosen line and let the live re-plan
   * find its way back to it. That cost a full pipeline run to choose between
   * two lines the engine had ALREADY returned — seconds of waiting, and the
   * route gone from the map while it ran. It could also come back subtly
   * different from the line that was clicked, because a via point is a hint
   * rather than an instruction.
   *
   * Nothing needs planning. Alternatives are only produced for a direct route
   * — `len(res.Legs) == 1` in the planning service — so there are no
   * intermediate stops whose times could shift. The whole difference between
   * these routes is one leg's geometry, its distance and its duration.
   *
   * `plan.alternatives` is deliberately left as the server sent it: the list
   * on screen is built from it once and must not be reordered by a choice, and
   * leaving it alone is also what tells the effect above that this was a
   * switch rather than a new plan.
   */
  const handleAlternativeClick = useCallback(
    (index: number) => {
      const chosen = ways?.list[index];
      if (!chosen) return;

      setPlan((current) => {
        // One leg is the shape alternatives are offered for; anything else
        // means that assumption has stopped holding, and swapping geometry
        // would draw a line that disagrees with the itinerary.
        if (!current || current.legs.length !== 1) return current;

        const leg = current.legs[0];
        const from = current.stops[0];

        /*
         * The end moves with the route; the start does not.
         *
         * Arrival times are minutes from the departure, so a longer or
         * shorter way round shifts everything after the first stop. With no
         * intermediate stops that is the final one, and its dwell is
         * whatever it already was.
         */
        const stops = current.stops.map((stop, at) => {
          if (at === 0 || !from) return stop;
          const arrivalMinutes = from.departureMinutes + chosen.durationMinutes;

          return {
            ...stop,
            arrivalMinutes,
            departureMinutes: arrivalMinutes + stop.dwellMinutes,
            arrivalTime: clockAfter(from.departureTime, chosen.durationMinutes),
            departureTime: clockAfter(
              from.departureTime, chosen.durationMinutes + stop.dwellMinutes,
            ),
          };
        });

        return {
          ...current,
          legs: [{
            ...leg,
            distanceMeters: chosen.distanceMeters,
            durationMinutes: chosen.durationMinutes,
            geometry: chosen.geometry,
          }],
          stops,
          totals: {
            ...current.totals,
            travelMinutes: chosen.durationMinutes,
            totalMinutes: chosen.durationMinutes + current.totals.dwellMinutes,
            distanceMeters: chosen.distanceMeters,
          },
        };
      });

      setWays((current) => (current ? { ...current, current: index } : current));
    },
    [ways],
  );

  /*
   * Using a place clicked on the map as a break.
   *
   * Offered only when the break is already switched on and the place is the
   * kind that break looks for — a café is no use as lunch, and neither is any
   * place at all if the traveller has not asked for a lunch stop.
   */
  const breakRoleFor = useCallback(
    (categoryKey: string): 'lunch' | 'coffee' | null => {
      if (breaksEnabled.lunch && BREAK_CATEGORIES.lunch.includes(categoryKey)) return 'lunch';
      if (breaksEnabled.coffee && BREAK_CATEGORIES.coffee.includes(categoryKey)) return 'coffee';
      return null;
    },
    [breaksEnabled],
  );

  const useAsBreak = useCallback(
    async (kind: 'lunch' | 'coffee') => {
      if (!picked) return;
      const chosen = picked;
      setPicked(null);
      try {
        // The dialog carries only what the map needed to draw the dot, so the
        // full record is fetched before it becomes the break's place.
        const poi = await api.poi(chosen.id);
        setBreakPlaces((current) => ({ ...current, [kind]: poi }));
        setToast(`${poi.name} set as the ${kind} stop`);
      } catch (err) {
        setToast((err as ApiError).message);
      }
    },
    [picked],
  );

  /**
   * Takes a place into the round, by id.
   *
   * The same two steps assignRole does for a map click, reachable from the
   * place panel: somebody browsing a POI has just read who has planned it
   * and how often, and the decision belongs where the reading happened
   * rather than back in the form.
   *
   * Refuses the round's own ends for the reason reservedAs gives, and says
   * nothing twice: a place already named is already in.
   */
  const addPlaceToRound = useCallback(
    async (poiId: string) => {
      const reserved = reservedAs(poiId);
      if (reserved) {
        setToast(reserved);

        return;
      }
      if (mustVisit.some((p) => p.id === poiId)) {
        setToast('That place is already in this round');

        return;
      }

      try {
        const poi = await api.poi(poiId);
        /* A named place is the traveller's choice, so it leaves the
           excluded list if a previous removal put it there. */
        setExcludedPoiIds((current) => current.filter((id) => id !== poiId));
        setMustVisit((current) => (
          current.some((p) => p.id === poi.id) ? current : [...current, poi]
        ));
        setToast(plan
          ? `${poi.name} added — updating route`
          : `${poi.name} added to places to visit`);
      } catch (err) {
        setToast((err as ApiError).message);
      }
    },
    [mustVisit, plan, reservedAs],
  );

  /** Turns a map click into a place in one of the planner's lists. */
  const assignRole = useCallback(
    async (role: 'visit' | 'avoid') => {
      if (!picked) return;
      setPicked(null);
      try {
        const poi = await api.poi(picked.id);
        if (role === 'visit') {
          const reserved = reservedAs(poi.id);
          if (reserved) {
            setToast(reserved);

            return;
          }
          setMustVisit((current) =>
            current.some((p) => p.id === poi.id) ? current : [...current, poi]);
          setToast(
            plan ? `${poi.name} added — updating route` : `${poi.name} added to places to visit`,
          );
        } else {
          setAvoid((current) =>
            current.some((p) => p.id === poi.id) ? current : [...current, poi]);
          setToast(plan ? `Avoiding ${poi.name} — updating route` : `Avoiding ${poi.name}`);
        }
      } catch (err) {
        setToast((err as ApiError).message);
      }
    },
    [picked, plan],
  );

  if (cities === null && !error) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (cities?.length === 0) {
    return (
      <Box sx={{ p: 4, maxWidth: 620, mx: 'auto' }}>
        <Alert severity="info" variant="outlined">
          <AlertTitle>No cities yet</AlertTitle>
          A city needs its map data imported before routes can be planned. Head
          to <strong>Admin</strong> to add one — you will need an OpenStreetMap
          extract URL for the metro area.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100%', minHeight: 0 }}>
      {/* ── category browser ─────────────────────────────────────── */}
      {showCategories && (
        <Paper
          variant="outlined"
          sx={{
            width: 234,
            flexShrink: 0,
            borderRadius: 0,
            borderTop: 0, borderBottom: 0, borderLeft: 0,
            overflowY: 'auto',
            display: { xs: 'none', lg: 'block' },
          }}
        >
          <CategoryPanel
            categories={categories}
            selected={selectedCategories}
            onChange={setSelectedCategories}
            colourFor={colourFor}
          />
        </Paper>
      )}

      {/* ── control panel ────────────────────────────────────────── */}
      <Paper
        ref={controlPanel}
        variant="outlined"
        sx={{
          width: { xs: '100%', md: 400 },
          flexShrink: 0,
          borderRadius: 0,
          borderTop: 0, borderBottom: 0, borderLeft: 0,
          overflowY: 'auto',
          p: 2.5,
          display: {
            xs: plan && !editing && !panelOnMobile ? 'none' : 'block',
            /*
             * Folded exactly when the traveller says so, and not otherwise.
             *
             * It used to refuse while the form held an edit, or before a first
             * route existed, unless the assistant happened to be open. That
             * was a guess about when folding would be unwelcome, and it made
             * the control that folds it do nothing for most of the time
             * somebody would press it. Nothing is lost either way — the form
             * is hidden, not unmounted, and the same button brings it back.
             */
            md: panelOpen ? 'block' : 'none',
          },
        }}
      >
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <TextField
              data-tour="city"
              select
              fullWidth
              label="City"
              value={city?.id ?? ''}
              onChange={(e) => setCity(cities?.find((c) => c.id === e.target.value) ?? null)}
            >
              {cities?.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {option.poiCount.toLocaleString('en-US')} places
                  </Typography>
                </MenuItem>
              ))}
            </TextField>
            <Tooltip title={showCategories ? 'Hide categories' : 'Show categories'}>
              <IconButton
                size="small"
                onClick={() => setShowCategories((v) => !v)}
                color={showCategories ? 'primary' : 'default'}
                sx={{ display: { xs: 'none', lg: 'inline-flex' } }}
              >
                <LayersOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          <Divider />

          {plan && !editing && (
            <>
              {/*
                One line on the panel, whatever is in it.

                A fifth control went in here and the row first broke "Change
                plan" across two lines inside its own button, then pushed
                Save onto a second row. Neither is acceptable for a toolbar
                somebody reads at a glance, so the labels are kept whole, the
                row is kept to one line at panel width, and the control that
                could afford to lose its label - Reset - gave it up.

                A phone is the exception: the panel is the whole screen there,
                the Map button joins the row, and wrapping beats clipping.
              */}
              <Stack
                direction="row"
                spacing={0.75}
                useFlexGap
                sx={{
                  flexWrap: { xs: 'wrap', md: 'nowrap' },
                  rowGap: 1,
                  alignItems: 'center',
                  '& .MuiButton-root': { whiteSpace: 'nowrap', flex: '0 0 auto' },
                }}
              >
                <Button startIcon={<ArrowBackIcon />} onClick={() => setEditing(true)} size="small">
                  Change plan
                </Button>
                {/* Narrow screens only, where the panel is covering the map. */}
                <Button
                  size="small"
                  color="inherit"
                  startIcon={<MapOutlinedIcon />}
                  onClick={() => setPanelOnMobile(false)}
                  sx={{ display: { xs: 'inline-flex', md: 'none' } }}
                >
                  Map
                </Button>
                {/* Its label is the first thing the row can spare. */}
                <Tooltip title="Start again from an empty form">
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setConfirmReset(true)}
                    aria-label="Reset the plan"
                  >
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {/*
                  Taking the route away with you.

                  Only with the request that made it: the turns are fetched
                  against the same costing and exclusions, and without them
                  the words could describe a line nobody was shown.
                */}
                {lastRequest && (
                  <ExportRouteButton
                    days={[{
                      label: '',
                      request: lastRequest,
                      plan,
                      ...(lastRequest.date ? { date: lastRequest.date } : {}),
                    }]}
                    /* Where it goes, not where it is: see endsTitle. A
                       saved round exports under the name it was saved with,
                       which the Saved page passes instead. */
                    name={endsTitle(plan, city?.name ?? 'Route')}
                    getMap={() => exportMap.current}
                  />
                )}
                <Box sx={{ flex: 1 }} />
                <Tooltip title="Hide this panel and give the map the space">
                  <IconButton
                    size="small"
                    onClick={() => setPanelOpen(false)}
                    aria-label="Hide the itinerary panel"
                    sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {/* Goes with the screen that reads them back — see TripPage. */}
                {FEATURES.savedTrips && (
                <Button
                  startIcon={<BookmarkAddOutlinedIcon />}
                  onClick={openSave}
                  size="small"
                  variant="outlined"
                >
                  Save
                </Button>
                )}
              </Stack>
              {/*
                What a longer day would have fitted, not offered.

                "14 places did not fit — 17 hr would fit 14 more" answers a
                question nobody asked: the day is five hours because that is
                the day, and a button setting it to seventeen is arithmetic
                dressed as advice. It also read as a failure at the top of a
                round that had planned perfectly well, which is the wrong
                first thing to see.

                Hidden rather than removed — the server still computes the
                suggestion and it is still on the response, so turning this
                back on is a one-word change.
              */}
              {SHOW_BUDGET_SUGGESTION && plan.suggestion && (
                <Alert
                  severity="warning"
                  variant="outlined"
                  action={
                    <Button
                      size="small"
                      color="warning"
                      variant="contained"
                      onClick={() => setAvailableMinutes(plan.suggestion!.availableMinutes)}
                    >
                      Use {formatBudget(plan.suggestion.availableMinutes)}
                    </Button>
                  }
                >
                  <AlertTitle sx={{ mb: 0.25 }}>
                    {plan.dropped.length} place{plan.dropped.length === 1 ? '' : 's'} did not fit
                  </AlertTitle>
                  <Typography variant="body2">
                    {formatBudget(plan.suggestion.availableMinutes)} would fit{' '}
                    {plan.suggestion.recovers} more.
                  </Typography>
                </Alert>
              )}

              {/*
                Manual adjustments, listed rather than left only on the map.
                A via point is a small diamond sitting on the route line, so
                deleting one by clicking is fiddly at best — and there needs
                to be a way back to an automatic route regardless.
              */}
              {(viaPoints.length > 0 || pinnedOrder) && (
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Stack
                    direction="row"
                    sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Adjusted by hand
                    </Typography>
                    <Button size="small" onClick={handleResetToAuto}>Reset to auto</Button>
                  </Stack>

                  {pinnedOrder && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label="Stop order pinned"
                      onDelete={() => setPinnedOrder(null)}
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  )}

                  {viaPoints.map((via, index) => (
                    <Chip
                      key={`${via.lat},${via.lng},${index}`}
                      size="small"
                      variant="outlined"
                      label={`Via ${via.lat.toFixed(4)}, ${via.lng.toFixed(4)}`}
                      onDelete={() => handleViaPointClick(index)}
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </Paper>
              )}

              {/*
                Offered beside the itinerary rather than in the form: a theme
                is an alternative to the day already on screen, so it only
                makes sense once there is one.
              */}
              {SHOW_THEMES && plan && (
                <ThemedAlternatives
                  draft={lastRequest ?? draft}
                  /*
                   * The day being offered alternatives TO, not the alternative
                   * currently previewed on it.
                   *
                   * Applying one replaces `plan`, so passing `plan` made every
                   * card measure itself against whichever alternative was last
                   * applied — and against ITSELF once applied, which scores
                   * 100% and printed "same road as the route on screen, the
                   * ground offers no other way" over a route visibly using
                   * different roads. It also moved the other cards' figures as
                   * a side effect of clicking one, so no two were comparable.
                   *
                   * `beforeTheme` is the route from before the first apply and
                   * is held across later ones, so every card is measured from
                   * the same baseline. It is also the right line to keep off
                   * and the right stop list to hold: an alternative is another
                   * way round the ORIGINAL day. Cleared on a re-plan, so a new
                   * day becomes the new baseline.
                   */
                  current={beforeTheme ?? plan}
                  hasStyle={(draft?.routeStyleKeys?.length ?? 0) > 0}
                  appliedKey={appliedTheme}
                  onApply={(themeKey, themed) => {
                    /*
                     * Shown, not silently adopted. The form still holds the
                     * request that produced the original day, so a later edit
                     * re-plans from that rather than from the theme — which is
                     * the honest behaviour, since the theme was a preview.
                     */
                    setBeforeTheme((kept) => kept ?? plan);
                    setPlan(themed);
                    setAppliedTheme(themeKey);
                    setToast(`Showing the ${themed.theme?.label ?? themeKey} route`);
                  }}
                  onRestore={beforeTheme ? () => {
                    setPlan(beforeTheme);
                    setBeforeTheme(null);
                    setAppliedTheme(null);
                    setToast('Back to the route you planned');
                  } : undefined}
                />
              )}

              {/*
                Other ways to run the same tasking, once an emphasis is set.
                Offered there because an emphasis says what the round is FOR
                and not that there is only one round that answers it — and
                because without one the form's own kinds of site are already
                the operator's answer to the same question.
              */}
              {plan && (lastRequest ?? draft)?.themeKey && (
                <RouteVariants
                  request={lastRequest ?? draft}
                  plan={plan}
                  appliedIndex={appliedVariant}
                  onApply={(variantPlan, excluded, index, variantRequest) => {
                    /*
                     * Adopted, not merely shown: the exclusions go into the
                     * form, so the next edit re-plans from the option chosen
                     * rather than quietly returning the one it replaced.
                     *
                     * And the option's own request becomes what was last
                     * sent. Without that, the form's draft — which now holds
                     * those exclusions — differs from the last attempt, the
                     * live re-plan fires, and it replaces the option that was
                     * just chosen with something else. Which looked, from the
                     * outside, like the list of options clearing itself.
                     */
                    setPlan(variantPlan);
                    setLastRequest(variantRequest);
                    setLastAttempt(variantRequest);
                    setExcludedPoiIds(excluded);
                    setAppliedVariant(index);
                    setToast(index < 0
                      ? 'Back to the route as first planned'
                      : `Showing option ${index + 2} — its exclusions are now on the form`);
                  }}
                />
              )}

              {city && (
                <ItineraryPanel
                  passedHidden={passedHidden}
                  basemapUrl={basemapUrlFor(city, settings['map.shared_basemap'] === true)}
                  {...(lastRequest
                    ? { directionsFor: directionsForPlan(lastRequest, plan) }
                    : {})}
                  plan={plan}
                  city={city}
                  onShowDetails={setDetailsPoiId}
                  onShowPoint={showPinnedStop}
                  onStopHover={setHighlighted}
                  /*
                    A row click takes the map to the stop and opens what is
                    known about it — the same two things a marker click does,
                    so the two views behave alike whichever is used.
                  */
                  onStopFocus={(index) => {
                    setHighlighted(index);
                    setFocusStop((current) => ({ index, nonce: (current?.nonce ?? 0) + 1 }));
                    const stop = plan?.stops[index];
                    if (stop?.poiId) setDetailsPoiId(stop.poiId);
                    else if (stop && stop.kind !== 'start' && stop.kind !== 'end') {
                      // A dropped pin has no place record; its own panel is
                      // what can be said about it.
                      showPinnedStop(index);
                    }
                  }}
                  /* The map, and nothing else: see onStopReveal. */
                  onStopReveal={(index) => {
                    setHighlighted(index);
                    setFocusStop((current) => ({ index, nonce: (current?.nonce ?? 0) + 1 }));
                  }}
                  revealStop={revealStop}
                  highlightedStopIndex={highlighted}
                  onReorder={handleReorder}
                  onDwellChange={handleDwellChange}
                  onChooseAlternative={handleAlternativeClick}
                {...(ways ? { waysRound: ways } : {})}
                  onHoverAlternative={setHoveredAlt}
                  availableMinutes={availableMinutes}
                  onRemoveStop={handleRemoveStop}
                  pendingRemoval={removedStops}
                  legModes={legModes}
                  onLegModeChange={handleLegModeChange}
                  {...(lastRequest?.date ? { coverageRoundDate: lastRequest.date } : {})}
                />
              )}
            </>
          )}

          {/*
            The form stays mounted while results are shown, just hidden.
            Unmounting it would discard everything the traveller typed.
          */}
          {city && (
            <Box sx={{ display: plan && !editing ? 'none' : 'block' }}>
              {plan && editing && (
                <Alert
                  severity="info"
                  variant="outlined"
                  sx={{ mb: 2 }}
                  action={
                    <Button size="small" onClick={() => setEditing(false)}>
                      Back
                    </Button>
                  }
                >
                  The map still shows your current route. It updates when you
                  plan again.
                </Alert>
              )}
              <PlannerForm
                key={formKey}
                city={city}
                styles={styles}
                submitting={submitting}
                onSubmit={handlePlan}
                onShowDetails={(poiId, role = 'visit') => setDetails({ poiId, role })}
                onSuggestionPreview={setSuggestionPreview}
                onCoverageChange={setCoverage}
                onRegionsChange={setRegions}
                {...(seed ? { seed } : {})}
                interestKeys={interestKeys}
                onInterestKeysChange={setInterestKeys}
                {...(corridor ? { corridorQuery: corridor } : {})}
                avoidCategoryCounts={avoidCategoryCounts}
                mustVisit={namedPlaces}
                orderHeldPoiIds={orderHeld}
                onBrowsePlaces={() => setBrowsing(true)}
                onMustVisitChange={(places) => {
                  // Only when the list GROWS: removing is handled where the
                  // stop is removed, and re-ordering here is not a thing the
                  // picker does.
                  if (places.length > namedPlaces.length) {
                    // Letting the order go also releases what was holding it.
                    unpinOrder();
                    setMustVisit(places);

                    return;
                  }
                  // The list it edited is the named half; the rest rides on.
                  setMustVisit([
                    ...places,
                    ...mustVisit.filter((p) => pinnedByOrder.has(p.id)),
                  ]);
                }}
                avoid={avoid}
                onAvoidChange={setAvoid}
                avoidDistanceM={avoidDistanceM}
                onAvoidDistanceChange={setAvoidDistanceM}
                categories={categories}
                avoidCategories={avoidCategories}
                onAvoidCategoriesChange={setAvoidCategories}
                availableMinutes={availableMinutes}
                onAvailableMinutesChange={setAvailableMinutes}
                start={start}
                onStartChange={setStart}
                startPin={startPin}
                onStartPinChange={setStartPin}
                end={end}
                onEndChange={setEnd}
                endPin={endPin}
                onEndPinChange={setEndPin}
                visitPins={visitPins}
                onVisitPinsChange={setVisitPins}
                avoidPins={avoidPins}
                onAvoidPinsChange={setAvoidPins}
                viaPoints={viaPoints}
                pinnedOrder={pinnedOrder}
                onReset={handleResetForm}
                stopDwellMinutes={stopDwellMinutes}
                legModes={legModes}
                onStopDwellChange={handleStopDwellOverride}
                breakMinutes={breakMinutes}
                onBreakMinutesChange={handleBreakMinutesChange}
                breaksEnabled={breaksEnabled}
                breakPlaces={breakPlaces}
                onBreakPlaceChange={(kind, poi) =>
                  setBreakPlaces((current) => ({ ...current, [kind]: poi }))}
                excludePoiIds={excludedPoiIds}
                colourFor={colourFor}
                avoidDistanceByPoiId={avoidDistanceByPoiId}
                avoidDistanceByCategoryKey={avoidDistanceByCategoryKey}
                onAvoidDistanceOverride={handleAvoidDistanceOverride}
                onBreaksEnabledChange={(kind, enabled) =>
                  setBreaksEnabled((current) => ({ ...current, [kind]: enabled }))}
                defaultStopMinutes={defaultStopMinutes}
                defaultSafetyPreference={settings['planner.default_safety_preference'] !== false}
                onExternalPick={handleExternalPick}
                onDraftChange={setDraft}
                requestedMode={requestedMode}
              />
            </Box>
          )}

          {error && <PlanError error={error} />}
        </Stack>
      </Paper>

      {/* ── map ──────────────────────────────────────────────────── */}
      <Box
        data-tour="map"
        sx={{
          flex: 1, minWidth: 0, position: 'relative',
          display: {
            xs: plan && !editing && !panelOnMobile ? 'block' : 'none',
            md: 'block',
          },
        }}
      >
        {/*
          The round on screen is out of date, said over the MAP.

          This lived at the top of the form, which is the one place it cannot
          be seen from: half the ways of changing a round are on the map —
          marking a spot, dragging a pin, excluding an area from the
          right-click menu — and all of them leave the panel scrolled
          somewhere else, collapsed, or hidden entirely behind "Map only".

          Floating and centred near the top, so it is in view however the
          change was made and whichever half of the screen is being used.
        */}
        {/*
          A break that was asked for and is not on the round.

          Shown here rather than beside the switch that set it: the itinerary
          is what somebody is reading when they notice lunch missing, and the
          form may be scrolled past, collapsed, or closed behind "Map only".
          Dismissable, because once it has been read the round is still the
          round — and remembered against THIS plan, so the next one asks again.
        */}
        {showBreakNotice && (
          <Alert
            severity="warning"
            variant="filled"
            onClose={() => setBreakNoticeClosed(plan)}
            sx={{
              position: 'absolute',
              top: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 4,
              alignItems: 'center',
              boxShadow: 4,
              maxWidth: 'calc(100% - 24px)',
            }}
          >
            {missedBreakLabel} {roundRuns}
            {breakWalledOff
              ? 'A place was found, but no road reaches it without entering an area '
                + 'you asked to keep clear of. Hold a smaller distance from those '
                + 'areas, or choose a place yourself.'
              : breakRanLate
                ? 'Move it earlier, or allow more time.'
                : 'Nothing suitable is available at this point in the route. '
                  + 'Try another time, allow more time, or choose a place yourself.'}
          </Alert>
        )}

        {staleForm && draft && (
          <Alert
            severity="info"
            variant="filled"
            sx={{
              position: 'absolute',
              top: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 4,
              alignItems: 'center',
              boxShadow: 4,
              maxWidth: 'calc(100% - 24px)',
            }}
            action={(
              <Button
                size="small"
                color="inherit"
                variant="outlined"
                disabled={planning}
                onClick={() => { setStaleForm(false); void handlePlan(draft, true); }}
              >
                {planning ? 'Planning…' : 'Plan again'}
              </Button>
            )}
          >
            {planning
              ? 'Your changes will need a new plan once this one finishes.'
              : 'Your changes are not on the map yet.'}
          </Alert>
        )}

        {/*
          On a narrow screen the map replaces the panel rather than sitting
          beside it, which took every control away with it — including the one
          that brings the panel back. This is the way back, and it exists only
          at that width because anywhere else the panel is already on screen.
        */}
        {plan && !editing && (
          <>
            <Button
              variant="contained"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => setPanelOnMobile(true)}
              sx={{
                display: { xs: 'inline-flex', md: 'none' },
                position: 'absolute',
                top: 12,
                left: 12,
                zIndex: 6,
                boxShadow: 3,
              }}
            >
              Itinerary
            </Button>

          </>
        )}

        {/*
          Fold the itinerary away, and bring it back. Both directions in one
          control, which is what it was missing: only the assistant's header
          could fold it, so seeing just the map meant opening a chat, and the
          way back sat in the bottom-left corner on top of the route's own
          figures.
        */}
        <MapOnlyToggle
          open={panelOpen}
          onToggle={() => setPanelOpen((open) => !open)}
          whatIsBeside="Itinerary"
        />

        {/*
          A background re-plan gives no other sign it is happening: the form
          stays usable and the old route stays drawn, so without this an edit
          looks like it did nothing until the route suddenly changes.
        */}
        {/*
          One indicator, not two.

          A hairline ran the full width of the map here as well. Two things
          animating for one wait is noise — and being full-bleed it read as
          part of the map's own chrome rather than as an answer to the button
          that was just pressed. The card says it, and says what is happening.
        */}
        <PlanningProgress open={planning} avoidCount={avoid.length + avoidCategories.length} />

        {/*
          Repeated over the map because that is where attention is once a
          route is on screen — and a failed re-plan leaves the previous route
          drawn, which on its own looks like nothing happened.
        */}
        {/*
          The route was drawn, and it does not keep every promise it was
          given.

          Before the planner started answering with the best route it could
          find, this case WAS an error and filled this banner. Now it plans —
          and the only sign was a red chip in a scrolled list and a line in
          the schedule behind the map. A route that passes what it was told to
          avoid, shown with nothing said, is worse than the refusal it
          replaced.
        */}
        {!error && shortfall.length > 0 && !shortfallDismissed && (
          <Alert
            severity="warning"
            variant="filled"
            onClose={() => setShortfallDismissed(true)}
            sx={{ position: 'absolute', top: 8, left: 8, right: 56, zIndex: 6 }}
          >
            <AlertTitle sx={{ mb: 0.25 }}>
              Not everything could be kept clear of
            </AlertTitle>
            <Typography variant="body2">{shortfall[0]}</Typography>
            {shortfall.length > 1 && (
              <Typography variant="caption">
                {shortfall.length - 1} more note{shortfall.length > 2 ? 's' : ''} with
                {' '}the schedule.
              </Typography>
            )}
          </Alert>
        )}

        {/*
          Bottom-left, clear of the stats strip and of the shortfall banner at
          the top: this is context for the line, not a thing to act on.
        */}
        {!error && roadNotes.length > 0 && !roadNoteHidden && (
          <Stack
            spacing={0.5}
            sx={{
              position: 'absolute', bottom: 52, left: 8, zIndex: 6,
              maxWidth: 'calc(100% - 16px)', alignItems: 'flex-start',
            }}
          >
            {roadNotes.map((note, i) => (
          <Chip
            key={note}
            size="small"
            label={note}
            /*
             * Dismissable, because it can be long.
             *
             * A note naming the roads a preference could not keep clear of
             * runs the width of the map and sits over the ground somebody may
             * be trying to read. It answers a question once; after that it is
             * in the way, and there was no way to put it down.
             *
             * Hidden for this plan only — `roadNoteHidden` is cleared when a
             * new one is drawn, since the next route's roads are a fresh
             * answer rather than the one already read.
             */
            // One control put them all down: they answer the same question
            // about the same line, and dismissing them one at a time would be
            // four clicks to clear what one glance read.
            onDelete={i === 0 ? () => setRoadNoteHidden(true) : undefined}
            sx={{
              maxWidth: '100%', bgcolor: 'background.paper', opacity: 0.94,
            }}
          />
            ))}
          </Stack>
        )}

        {error && (
          <Alert
            severity={error.code === 'NO_ROUTE' || error.code === 'NO_ROUTE_AVOIDANCE'
              ? 'warning' : 'error'}
            variant="filled"
            onClose={() => setError(null)}
            sx={{ position: 'absolute', top: 8, left: 8, right: 56, zIndex: 6 }}
          >
            <AlertTitle sx={{ mb: 0.25 }}>
              {error.code === 'NO_ROUTE_AVOIDANCE'
                ? 'No route avoids everything you asked'
                : error.code === 'AVOIDANCE_TOO_LARGE'
                  ? 'Too much area excluded'
                  : error.code === 'TOO_FAR_FOR_STOPS'
                    ? 'Too far apart to choose stops along'
                    : error.code === 'NO_ROAD_BETWEEN'
                      ? 'No road between these two places'
                      : 'Could not plan this route'}
            </AlertTitle>
            <Typography variant="body2">{error.message}</Typography>
            {routeAdvice(error).reasons.map((reason) => (
              <Typography key={reason} variant="body2" sx={{ mt: 0.25 }}>{reason}</Typography>
            ))}
            {routeAdvice(error).suggestions.length > 0 && (
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {routeAdvice(error).suggestions.map((suggestion) => (
                  <li key={suggestion}>
                    <Typography variant="caption">{suggestion}</Typography>
                  </li>
                ))}
              </ul>
            )}
            {workingMode(error) && (
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                sx={{ mt: 1 }}
                /*
                 * Clearing the error here would be premature: changing the
                 * mode triggers a re-plan, which replaces it either way.
                 */
                onClick={() => setRequestedMode((prev) => ({
                  mode: workingMode(error)!,
                  token: (prev?.token ?? 0) + 1,
                }))}
              >
                Plan it by {workingMode(error)}
              </Button>
            )}
            {plan && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.9 }}>
                Still showing the last route that worked.
              </Typography>
            )}
          </Alert>
        )}

        {city && (
          <MapView
            passedHidden={passedHidden}
            onPassedHiddenChange={setPassedHidden}
            onMapReady={(map) => { exportMap.current = map; }}
            onCamera={(at) => watchView(at.bounds)}
            city={city}
            plan={plan}
            basemapUrl={basemapUrlFor(city, settings['map.shared_basemap'] === true)}
            poiData={poiData}
            characterPoiData={characterPlaces}
            previewAvoid={previewAvoid}
            coverage={coverage}
            regions={regions}
            categoryColours={categoryColours}
            onPoiClick={setPicked}
            onMapClick={setMarkedPoint}
            pins={mapPins}
            /*
             * Opens the place behind a suggested ring, in the dialog that can
             * then put it in the round.
             *
             * The role travels with the markers rather than being guessed at
             * here: the same ring means "add this stop" when the places list
             * drew it and "use this for lunch" when the lunch list did, and
             * the dialog's button has to say which.
             */
            onPinClick={(poiId) => setDetails({ poiId, role: suggestionPreview.role })}
            onPinHint={() => setToast('Double-click a marked spot to remove it')}
            roundTrip={draftIsRoundTrip}
            onPinDrag={handlePinDrag}
            /* Dragging an end of the drawn route moves it and re-plans. */
            onEndpointDrag={handleEndpointDrag}
            /* Dragging the line itself bends the route through that point. */
            onRouteBend={bendRoute}
            onViaPointMove={moveViaPoint}
            viaPoints={viaPoints}
            onViaPointClick={handleViaPointClick}
            onViaPointHint={() => setToast('Double-click a via point to remove it')}
            /*
              Fed from the list on screen, not from the server's array.

              The rows are [the drawn route, ...plan.alternatives], so row N
              is alternative N-1 — and hovering a row passed its ROW number as
              the alternative to light up. Every preview was therefore one
              route out, and the first row, being the drawn leg, was not in
              the server's array at all and could never be previewed.

              Invisible while every alternative was drawn and only the hovered
              one thickened; fatal once the hovered one is the only one drawn.
              Keyed by position here, which is what both the hover and the
              click already speak.
            */
            alternatives={altLines}
            onAlternativeClick={handleAlternativeClick}
            highlightedAlternative={hoveredAlt}
            highlightedStopIndex={highlighted}
            /*
             * Highlights and opens the place panel together.
             *
             * Reaching a marker on the map and then hunting the same stop in
             * the itinerary to read about it is work the click can do itself.
             * A dropped pin has no place behind it, so it only highlights.
             */
            onStopClick={(index) => {
              setHighlighted(index);
              // And scroll the itinerary to it, so the list agrees with the
              // marker that was just picked.
              setRevealStop((current) => ({ index, nonce: (current?.nonce ?? 0) + 1 }));
              const poiId = plan?.stops[index]?.poiId;
              if (poiId) setDetailsPoiId(poiId);
            }}
            focusStop={focusStop}
          />
        )}
      </Box>

      {/* ── role picker for a clicked POI ────────────────────────── */}
      <PlacePickerDialog
        picked={picked}
        onClose={() => setPicked(null)}
        onAssign={(role) => void assignRole(role)}
        breakRole={picked ? breakRoleFor(picked.categoryKey) : null}
        onUseAsBreak={(kind) => void useAsBreak(kind)}
      />

      {/* ── role picker for a spot marked on the map ─────────────── */}
      <Dialog open={Boolean(markedPoint)} onClose={() => setMarkedPoint(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>Mark this spot</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {markedPoint && `${markedPoint.lat.toFixed(5)}, ${markedPoint.lng.toFixed(5)}`}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Stack spacing={1}>
            <Button variant="contained" onClick={() => markSpot('start')}>Set as start</Button>
            <Button variant="contained" onClick={() => markSpot('end')}>Set as destination</Button>
            {/*
              A stop only once the round has two ends.

              A stop is somewhere the route passes on its way between them,
              and before both are set there is no way and nothing for it to
              be between: it went into the list and sat there, doing nothing,
              until an operator wondered why marking spots had no effect. The
              two buttons above are how the ends get set, so the offer comes
              back the moment they are.
            */}
            {hasBothEnds && (
              <Button variant="outlined" onClick={() => markSpot('visit')}>Add as a stop</Button>
            )}
            {/*
              Only once there is a route to bend. Before one exists there is
              no line to route through and no leg to attach it to, so the
              button would be an offer that could not be kept.
            */}
            {plan && (
              <Button variant="outlined" onClick={routeThroughSpot}>
                Route through here
              </Button>
            )}
            {/*
              And an exclusion only once there is a round to hold it clear of.
              Same reason as the stop above: before both ends are set there is
              no route to keep away from anything.
            */}
            {hasBothEnds && (
              <Button variant="outlined" color="warning" onClick={() => markSpot('avoid')}>
                Avoid this area
              </Button>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkedPoint(null)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* ── name and save the trip ───────────────────────────────── */}
      <Dialog
        open={saveName !== null}
        onClose={() => !saving && setSaveName(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 0.5 }}>Save this trip</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Name"
            value={saveName ?? ''}
            onChange={(event) => setSaveName(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') void handleSave(); }}
            sx={{ mt: 1 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
            Saved trips are kept on the server and stay available after a
            refresh. They are tied to this browser.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveName(null)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={saving || !saveName?.trim()}
          >
            {saving ? 'Saving…' : 'Save trip'}
          </Button>
        </DialogActions>
      </Dialog>

      {/*
        Asked only here. This button sits between "Change plan" and "Save",
        and a misclick would throw away a finished route; the same action
        inside the form has nothing built up to lose.
      */}
      <Dialog open={confirmReset} onClose={() => setConfirmReset(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>Clear everything?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This removes the route, the places you chose and any adjustments you
            made by hand. Saved routes are not affected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReset(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => { setConfirmReset(false); handleResetForm(); }}
          >
            Clear everything
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        message={toast}
      />
      <PinnedStopDialog
        stop={pinIndex === null ? null : plan?.stops[pinIndex] ?? null}
        cityId={city?.id ?? null}
        onClose={() => setPinIndex(null)}
        onCopied={setToast}
      />

      {/*
        A place panel reads, and now decides.

        It once carried an Add button that could fail in ways the form could
        not explain — a full day offering a choice between dropping a stop
        and lengthening it — so it was reduced to reading only, with naming
        done in the form.

        What changed is what the panel knows: its Coverage tab now says who
        has planned this place, how often, and in which rounds. That is
        exactly the reading somebody does while deciding whether to take it,
        and sending them back to the form to type a name they are looking at
        is the long way round. So the one action belongs here again — add
        where the place is not in the round, remove where it is, and never
        both.
      */}
      {/*
        The catalogue, with the record beside it.

        Mounted only while it is open: it holds a search, a filter and a
        coverage fetch, none of which a closed window should be running.
      */}
      {browsing && city && (
        <PlaceChooserDialog
          open
          city={city}
          categories={categories}
          basemapUrl={basemapUrlFor(city, settings['map.shared_basemap'] === true)}
          onClose={() => setBrowsing(false)}
          /* The place's own record, over the browser: photographs, hours,
             and what is there. Its own window, as everywhere else. */
          onSetStart={(poi) => {
            setStartPin(null);
            setStart(poi);
            setToast(`Starting at ${poi.name}`);
          }}
          onSetEnd={(poi) => {
            setEndPin(null);
            setEnd(poi);
            setToast(`Ending at ${poi.name}`);
          }}
          onAddStop={(poi) => { void addPlaceToRound(poi.id); }}
          inRound={mustVisit.map((p) => p.id)}
          {...(start ? { startId: start.id } : {})}
          {...(end ? { endId: end.id } : {})}
        />
      )}

      <PlaceDetailsDialog
        poiId={detailsPoiId}
        onClose={() => setDetails(null)}
        city={city}
        {...(city ? { basemapUrl: basemapUrlFor(city, settings['map.shared_basemap'] === true) } : {})}
        {...(droppableStop >= 0
          ? { onRemoveFromRound: () => handleRemoveStop(droppableStop) }
          : { onAddToRound: (id: string) => { void addPlaceToRound(id); } })}
      />

      {/*
        The assistant floats over the app rather than living in the form.

        It talks about whatever is on screen — the map, the itinerary, the
        fields — so putting it inside one of those would have hidden it
        exactly when it is being used. Bottom right, above the map, out of the
        way until asked for, and closable back to a button.
      */}
      {/*
        The planning assistant, hidden in a production build.

        Both the button and the panel go together: the panel is mounted
        by the button and by nothing else, so one test covers the whole
        feature. See src/config/features.ts.
      */}
      {city && FEATURES.planningAgent && assistantEnabled && (
        <Box
          sx={{
            /*
             * 10px, which is MapLibre's own control margin — so this button
             * lines up with the attribution beneath it and the zoom control
             * above, instead of sitting a few pixels inside them and reading
             * as a near-miss.
             */
            position: 'fixed', right: 10,
            /*
             * Clear of the map's own bottom-right furniture.
             *
             * MapLibre puts the attribution there — compact, so it collapses
             * to a single info button — and the round-trip chip sits beside
             * it. This button is fixed to the viewport and knew nothing about
             * either, so it landed on top of both. The credits in particular
             * are a licence condition, and one under a button is not shown.
             *
             * Raised rather than moving the attribution: the map's other
             * bottom corner already carries the plan legend and the
             * click-to-add hint, so there is nowhere for it to go.
             */
            bottom: { xs: 44, sm: 52 },
            zIndex: (theme) => theme.zIndex.drawer + 2,
            display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1,
            maxWidth: 'calc(100vw - 24px)',
          }}
        >
          {/*
            Mounted once opened, and hidden rather than unmounted after that.

            Closing it used to throw the panel away: the session, the
            transcript and the scroll position went with it, and reopening
            fetched a conversation again — sometimes a different one, always
            an empty-looking pause. Shutting a chat window is not asking to
            forget the conversation.
          */}
          {assistantEverOpen && (
            <Box
              sx={{
                width: { xs: 'calc(100vw - 24px)', sm: 420 },
                display: assistantOpen ? 'block' : 'none',
              }}
            >
              <AssistantPanel
                cityId={city.id}
                cityName={city.name}
                draft={draft}
                plan={plan}
                onApply={handleAssistantResult}
                onTrip={handleAssistantTrip}
                formVisible={panelOpen}
                onToggleForm={() => setPanelOpen((open) => !open)}
                /*
                  Cleared, and it STAYS cleared.

                  This used to reset `clearedAt` to 0 straight after clearing
                  the form, which switched off the very guard the clear had
                  just armed. Anything the old conversation still had in
                  flight — a turn that had been thinking for half a minute —
                  then arrived and filled the form back in with its start, its
                  destination and its hours, over a form the operator had just
                  emptied and a conversation that had said nothing.

                  Zeroing was never needed. The guard compares the clear
                  against when each TURN began, so a turn started after this
                  one is not stale and applies normally; only one that predates
                  the clear is refused.
                */
                onNewConversation={handleResetForm}
                onClose={() => setAssistantOpen(false)}
              />
            </Box>
          )}
          <Fab
            data-tour="assistant"
            color="primary"
            variant={assistantOpen ? 'circular' : 'extended'}
            onClick={() => {
              setAssistantEverOpen(true);
              setAssistantOpen((open) => !open);
            }}
            sx={{ alignSelf: 'flex-end' }}
          >
            {assistantOpen ? <CloseIcon /> : (
              <>
                <AutoAwesomeIcon sx={{ mr: 1 }} />
                Plan by asking
              </>
            )}
          </Fab>
        </Box>
      )}
    </Box>
  );
}

/**
 * Avoidance failures are the interesting case: scope §8 requires that an
 * impossible route names the constraint responsible so the traveller can
 * relax it, rather than failing opaquely.
 */
/**
 * Pulls the server's explanation out of a failed plan.
 *
 * "No route could be found" is true of a point in a river, a point on a
 * private drive, and a walk down a motorway alike, and each wants a different
 * fix from the traveller. The planner works out which and says so; this reads
 * what it said. Older errors carry nothing, so both lists may be empty and the
 * message stands alone.
 */
function routeAdvice(error: ApiError): { reasons: string[]; suggestions: string[] } {
  const details = error.details as
    { reasons?: unknown; suggestions?: unknown } | null | undefined;
  const strings = (value: unknown) =>
    Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
  return {
    // The first reason is already the alert's own message; showing it twice
    // reads as a stutter.
    reasons: strings(details?.reasons).slice(1),
    suggestions: strings(details?.suggestions),
  };
}

/** The mode the planner found does reach, when the chosen one does not. */
function workingMode(error: ApiError): TravelMode | null {
  const found = (error.details as { worksWithMode?: unknown } | null)?.worksWithMode;
  return typeof found === 'string' ? (found as TravelMode) : null;
}

function PlanError({ error }: { error: ApiError }) {
  if (error.code === 'NO_ROUTE_AVOIDANCE') {
    const conflicting = (error.details as { conflicting?: { name: string; radiusM: number }[] })
      ?.conflicting;
    return (
      <Alert severity="warning" variant="outlined">
        <AlertTitle>No route avoids everything you asked</AlertTitle>
        {conflicting?.length ? (
          <>
            These are most likely in the way:
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {conflicting.map((zone) => (
                <li key={zone.name}>{zone.name} — {zone.radiusM} m</li>
              ))}
            </ul>
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              Remove one, or reduce the avoidance distance.
            </Typography>
          </>
        ) : (
          'Try removing a place to avoid, or reducing the distance.'
        )}
      </Alert>
    );
  }

  if (error.code === 'AVOIDANCE_TOO_LARGE') {
    return (
      <Alert severity="warning" variant="outlined">
        <AlertTitle>Too much area excluded</AlertTitle>
        {error.message}
      </Alert>
    );
  }

  const { reasons, suggestions } = routeAdvice(error);

  return (
    <Alert severity="warning" variant="outlined">
      <AlertTitle>Could not plan this route</AlertTitle>
      {error.message}
      {reasons.map((reason) => (
        <Typography key={reason} variant="body2" sx={{ mt: 0.5 }}>{reason}</Typography>
      ))}
      {suggestions.length > 0 && (
        <>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, fontWeight: 600 }}>
            What to try
          </Typography>
          <ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>
            {suggestions.map((suggestion) => (
              <li key={suggestion}>
                <Typography variant="caption">{suggestion}</Typography>
              </li>
            ))}
          </ul>
        </>
      )}
    </Alert>
  );
}

/** "3 hr 45 min" — friendlier than a raw minute count in a button. */
function formatBudget(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}
